/**
 * Wirtschaft & Finanzen — Cash-Flow, Gebäude, Subsidiaries, Fahrzeuge, Lager
 */
import { formatCash, state } from '../main';
import { fixture } from '../data/fixture';
import { t } from '../i18n';
import { iconForCategory, iconNetWorth, iconIncome, iconExpenses, iconAuditRisk, CATEGORY_COLORS } from '../icons/economy';
import { AUDIT_THRESH_WARN, AUDIT_THRESH_CRIT, EXPENSE_WARN_PCT, EXPENSE_CRIT_PCT } from '../config/thresholds';
import { pathRevenue, pathDepartment, pathAudit } from '../canvas/economy-icons';
import { mapData } from '../data/fixture';
import { portraitHTML, hydratePortraits } from '../scene/PortraitEngine';
import type { RunnerDTO, BalanceCategory } from '../data/fixture';
import { balanceCategoryLabel, balanceDetailLabel } from '../utils/balance-label';
import { describeDelta, previousSnapshot, renderDeltaHTML } from '../utils/economy-diff';
import { submitPlan, getEconomyHistory, getEconomyForecast, getEconomyPeers } from '../api';
import type { EconomySnapshot, EconomyPeersResponse } from '../api';
import { nav } from '../main';
import { renderMiniSparkline, categorySeries } from '../utils/mini-sparkline';
import { escHtml } from './hub';

// ── Canvas Pipeline Types ──────────────────────────────────────────────

// ── Economy history cache ───────────────────────────────────────────────
//
// Per-session ring-buffer of EconomySnapshot fetched from
// /api/sessions/{id}/economy/history (server v1.10.2+). Used by the
// overview tab for vs-previous-cycle diffs and by the deep-dive tabs
// for sparklines. Kept at module scope so tab switches don't re-fetch.
//
// Staleness: fetched on mount, refreshed after 5 minutes OR when the
// session changes. Good enough — the server only appends once per
// day-resolve; intra-day data is stable.

interface HistoryCache {
  sessionId: string;
  fetchedAt: number;
  data: EconomySnapshot[];
}
let historyCache: HistoryCache | null = null;
const HISTORY_TTL_MS = 5 * 60 * 1000;

function getCachedHistory(sessionId: string): EconomySnapshot[] {
  if (!historyCache || historyCache.sessionId !== sessionId) return [];
  return historyCache.data;
}

/** Fire-and-forget fetch. On success, calls onUpdate with fresh data so
 *  the caller can re-render diff indicators in-place. */
function refreshHistory(sessionId: string, onUpdate: (data: EconomySnapshot[]) => void): void {
  if (!sessionId) return;
  const fresh = historyCache
    && historyCache.sessionId === sessionId
    && Date.now() - historyCache.fetchedAt < HISTORY_TTL_MS;
  if (fresh) {
    onUpdate(historyCache!.data);
    return;
  }
  getEconomyHistory(sessionId).then(resp => {
    historyCache = { sessionId, fetchedAt: Date.now(), data: resp.snapshots ?? [] };
    onUpdate(historyCache.data);
  }).catch(() => {
    // Server may be on v1.10.1 (pre-history endpoint) — degrade silently.
  });
}

export type PipelineRole = 'revenue' | 'department' | 'audit' | 'clean' | 'dirty';

/**
 * Pure helper: returns the ordered role list rendered by drawPipeline.
 * DEPARTMENT is dropped when the player has zero Tarnfirmen (empty-sub
 * collapse — otherwise the node would render as an empty placeholder
 * and waste horizontal space). Extracted for unit-testing without a
 * canvas context.
 */
export function pipelineLayout(subsCount: number): PipelineRole[] {
  const roles: PipelineRole[] = ['revenue'];
  if (subsCount > 0) roles.push('department');
  roles.push('audit', 'clean', 'dirty');
  return roles;
}

interface PipelineNode {
  /** Stable role identifier so we can hit-test + style by semantic
   *  instead of array index (nodes[] is dynamic — DEPARTMENT drops
   *  out when the player has zero Tarnfirmen). */
  role: PipelineRole;
  x: number; y: number; w: number; h: number;
  label: string; value: string; color: string; glowColor: string;
  extra?: string[];
  tooltip?: string[];
  path?: Path2D;
}

interface Particle { t: number; speed: number; color: string; }

// ── Bézier helper ──────────────────────────────────────────────────────

function bezierPoint(p0: number[], p1: number[], p2: number[], p3: number[], t: number): [number, number] {
  const u = 1 - t;
  return [
    u*u*u*p0[0] + 3*u*u*t*p1[0] + 3*u*t*t*p2[0] + t*t*t*p3[0],
    u*u*u*p0[1] + 3*u*u*t*p1[1] + 3*u*t*t*p2[1] + t*t*t*p3[1],
  ];
}

// ── Draw rounded rect ──────────────────────────────────────────────────

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// ── Pipeline Canvas Renderer ───────────────────────────────────────────

interface PipelineData {
  incomeLines: BalanceCategory[];
  subsCount: number; subNames: string[];
  auditPct: number; federalHeat: number;
  cleanCash: number; dirtyCash: number;
  dailyIncome: number;
}

function drawPipeline(
  canvas: HTMLCanvasElement,
  data: PipelineData,
  timestamp: number,
  particles: Particle[][],
  hoveredNode = -1,
): PipelineNode[] {
  const ctx = canvas.getContext('2d');
  if (!ctx) return [];

  const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1;
  const W = canvas.clientWidth || 760;
  const H = 180;
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  canvas.style.width = W + 'px';
  canvas.style.height = H + 'px';
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, W, H);

  // Node dimensions — Revenue wider for long labels like "Legale Einnahmen"
  const nodeH = 100, nodeR = 6;
  // Measure longest revenue label to size Revenue node dynamically
  ctx.font = '10px "Share Tech Mono", monospace';
  const top3Labels = [...data.incomeLines].sort((a, b) => b.amount - a.amount).slice(0, 3);
  const maxLabelW = top3Labels.reduce((m, l) => {
    const labelW = ctx.measureText(balanceCategoryLabel(l)).width + ctx.measureText('+' + formatCash(Math.round(l.amount))).width + 28; // dot + padding
    return Math.max(m, labelW);
  }, 0);
  const revenueW = Math.max(140, Math.min(200, maxLabelW + 12));
  const stdW = 120; // Department, Audit
  const endW = 115; // Clean/Dirty Cash

  // Collapse the DEPARTMENT node when the player has no Tarnfirmen.
  // An empty Tarnfirmen placeholder adds noise without signal — Revenue
  // flows straight into Audit when there's nothing to launder. Player
  // can still access sub setup via the SUBSIDIARIES tab.
  const hasDept = data.subsCount > 0;

  const deptSlots = hasDept ? 1 : 0;
  const totalNodesW = revenueW + stdW * deptSlots + stdW /* audit */ + endW;
  const gapSlots = hasDept ? 5 : 4; // one extra gap per node
  const gap = Math.max(12, (W - totalNodesW) / gapSlots);
  const midY = H / 2 - nodeH / 2;

  // Node positions — skip DEPARTMENT column entirely when no subs.
  const xRev   = gap;
  const xDept  = xRev + revenueW + gap;
  const xAudit = hasDept ? (xDept + stdW + gap) : (xRev + revenueW + gap);
  const xEnd   = xAudit + stdW + gap;
  // Heat-map coloring: heat 0→green, 50→amber, 100→red
  const heat = data.federalHeat;
  const heatColor = heat < 30 ? '#2ecc71' : heat < 60 ? '#fca311' : '#e74c3c';
  const heatGlow = heat < 30 ? 'rgba(46,204,113,0.25)' : heat < 60 ? 'rgba(252,163,17,0.3)' : 'rgba(231,76,60,0.4)';

  // Tooltip content per node
  const top3Rev = [...data.incomeLines].sort((a, b) => b.amount - a.amount).slice(0, 3);
  const revTooltip = ['Tageseinnahmen: ' + formatCash(Math.round(data.dailyIncome)), ...top3Rev.map(l => `  ${balanceCategoryLabel(l)}: +${formatCash(Math.round(l.amount))}`)];
  const deptTooltip = [`${data.subsCount} Tarnfirma${data.subsCount !== 1 ? 'n' : ''}`, ...data.subNames.slice(0, 5).map(n => `  ${n}`)];
  const auditTooltip = [`Audit-Risiko: ${data.auditPct}%`, `Federal Heat: ${Math.round(heat)}/100`, heat >= 60 ? '⚠ KRITISCH' : heat >= 30 ? '⚠ Erhöht' : '✓ Niedrig'];
  const cleanTooltip = ['Sauberes Kapital', formatCash(Math.round(data.cleanCash))];
  const dirtyTooltip = ['Schmutziges Kapital', formatCash(Math.round(data.dirtyCash)), data.dirtyCash > 0 ? '⚠ Audit-Risiko' : '✓ Kein Risiko'];

  const nodes: PipelineNode[] = [
    { role: 'revenue', x: xRev, y: midY, w: revenueW, h: nodeH, label: 'REVENUE', value: formatCash(Math.round(data.dailyIncome)), color: '#3498db', glowColor: 'rgba(52,152,219,0.3)', tooltip: revTooltip },
  ];
  if (hasDept) {
    nodes.push({ role: 'department', x: xDept, y: midY, w: stdW, h: nodeH, label: 'DEPARTMENT', value: `${data.subsCount} Sub${data.subsCount !== 1 ? 's' : ''}`, color: '#fca311', glowColor: 'rgba(252,163,17,0.25)', tooltip: deptTooltip });
  }
  nodes.push(
    { role: 'audit', x: xAudit, y: midY, w: stdW, h: nodeH, label: 'AUDIT', value: `${data.auditPct}%`, color: heatColor, glowColor: heatGlow, tooltip: auditTooltip },
    { role: 'clean', x: xEnd, y: midY - 16, w: endW, h: 58, label: 'CLEAN CASH', value: formatCash(Math.round(data.cleanCash)), color: '#2ecc71', glowColor: 'rgba(46,204,113,0.3)', tooltip: cleanTooltip },
    { role: 'dirty', x: xEnd, y: midY + 58, w: endW, h: 58, label: 'DIRTY CASH', value: formatCash(Math.round(data.dirtyCash)), color: '#e74c3c', glowColor: 'rgba(231,76,60,0.3)', tooltip: dirtyTooltip },
  );

  // Find nodes by role for connector wiring.
  const nodeByRole = new Map<PipelineRole, PipelineNode>();
  for (const n of nodes) nodeByRole.set(n.role, n);
  const revNode   = nodeByRole.get('revenue')!;
  const deptNode  = nodeByRole.get('department'); // optional
  const auditNode = nodeByRole.get('audit')!;
  const cleanNode = nodeByRole.get('clean')!;
  const dirtyNode = nodeByRole.get('dirty')!;

  // Draw connector Béziers — chain Revenue → (Department?) → Audit, then
  // fork Audit → Clean / Dirty. Colors pair start-end per segment.
  const connectors: [number[], number[], number[], number[], boolean][] = [];
  const connectorColors: [string, string][] = [];
  const linkStraight = (from: PipelineNode, to: PipelineNode, isDirty: boolean, colors: [string, string]): void => {
    const p0 = [from.x + from.w, from.y + from.h / 2];
    const p3 = [to.x, to.y + to.h / 2];
    const cp1 = [p0[0] + 20, p0[1]];
    const cp2 = [p3[0] - 20, p3[1]];
    connectors.push([p0, cp1, cp2, p3, isDirty]);
    connectorColors.push(colors);
  };
  if (deptNode) {
    linkStraight(revNode, deptNode, false, ['rgba(52,152,219,0.5)', 'rgba(252,163,17,0.5)']);
    linkStraight(deptNode, auditNode, false, ['rgba(252,163,17,0.5)', 'rgba(155,89,182,0.5)']);
  } else {
    // Revenue → Audit direct (no Tarnfirmen to launder through)
    linkStraight(revNode, auditNode, false, ['rgba(52,152,219,0.5)', 'rgba(155,89,182,0.5)']);
  }
  // Audit → Clean / Dirty fork
  const auditRight = [auditNode.x + auditNode.w, auditNode.y + auditNode.h / 2];
  const cleanLeft = [cleanNode.x, cleanNode.y + cleanNode.h / 2];
  const dirtyLeft = [dirtyNode.x, dirtyNode.y + dirtyNode.h / 2];
  connectors.push([auditRight, [auditRight[0] + 20, auditRight[1]], [cleanLeft[0] - 20, cleanLeft[1]], cleanLeft, false]);
  connectorColors.push(['rgba(155,89,182,0.5)', 'rgba(46,204,113,0.5)']);
  connectors.push([auditRight, [auditRight[0] + 20, auditRight[1]], [dirtyLeft[0] - 20, dirtyLeft[1]], dirtyLeft, true]);
  connectorColors.push(['rgba(155,89,182,0.5)', 'rgba(231,76,60,0.5)']);

  // Draw connectors with animated dash + gradients
  const dashOffset = (timestamp / 300) % 10;
  connectors.forEach(([p0, cp1, cp2, p3, isDirty], ci) => {
    ctx.beginPath();
    ctx.moveTo(p0[0], p0[1]);
    ctx.bezierCurveTo(cp1[0], cp1[1], cp2[0], cp2[1], p3[0], p3[1]);
    const dirtyDimmed = isDirty && data.dirtyCash <= 0;
    const grad = ctx.createLinearGradient(p0[0], p0[1], p3[0], p3[1]);
    const colors = connectorColors[ci] ?? ['rgba(100,120,140,0.4)', 'rgba(100,120,140,0.4)'];
    grad.addColorStop(0, dirtyDimmed ? 'rgba(231,76,60,0.1)' : colors[0]);
    grad.addColorStop(1, dirtyDimmed ? 'rgba(231,76,60,0.1)' : colors[1]);
    ctx.strokeStyle = grad;
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.lineDashOffset = -dashOffset;
    ctx.stroke();
    ctx.setLineDash([]);
  });

  // Draw particles along connectors
  if (particles.length === 0) {
    for (let i = 0; i < connectors.length; i++) {
      const isDirty = connectors[i][4];
      const pArr: Particle[] = [];
      for (let j = 0; j < 4; j++) {
        pArr.push({ t: j * 0.25, speed: 0.00015 + Math.random() * 0.00008, color: isDirty ? '#e74c3c' : '#2ecc71' });
      }
      particles.push(pArr);
    }
  }

  connectors.forEach(([p0, cp1, cp2, p3, isDirty], ci) => {
    const pArr = particles[ci];
    if (!pArr) return;
    // Skip dirty particles when no dirty cash
    if (isDirty && data.dirtyCash <= 0) return;
    for (const p of pArr) {
      p.t = (p.t + p.speed * 16) % 1;
      // Glow trail — 3 fading circles behind the particle
      for (let trail = 3; trail >= 0; trail--) {
        const trailT = (p.t - trail * 0.015 + 1) % 1;
        const [tx, ty] = bezierPoint(p0, cp1, cp2, p3, trailT);
        const alpha = trail === 0 ? 1 : 0.15 * (3 - trail);
        const radius = trail === 0 ? 2.5 : 2 - trail * 0.3;
        ctx.beginPath();
        ctx.arc(tx, ty, radius, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = alpha;
        if (trail === 0) {
          ctx.shadowColor = p.color;
          ctx.shadowBlur = 8;
        }
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 1;
      }
    }
  });

  // Draw nodes — build Path2D per node for hit-testing
  nodes.forEach((node, i) => {
    // Build Path2D for isPointInPath hit-testing (try-catch: Happy-DOM stubs Path2D without methods)
    try {
      const nodePath = new Path2D();
      const r = nodeR;
      nodePath.moveTo(node.x + r, node.y);
      nodePath.lineTo(node.x + node.w - r, node.y);
      nodePath.quadraticCurveTo(node.x + node.w, node.y, node.x + node.w, node.y + r);
      nodePath.lineTo(node.x + node.w, node.y + node.h - r);
      nodePath.quadraticCurveTo(node.x + node.w, node.y + node.h, node.x + node.w - r, node.y + node.h);
      nodePath.lineTo(node.x + r, node.y + node.h);
      nodePath.quadraticCurveTo(node.x, node.y + node.h, node.x, node.y + node.h - r);
      nodePath.lineTo(node.x, node.y + r);
      nodePath.quadraticCurveTo(node.x, node.y, node.x + r, node.y);
      nodePath.closePath();
      node.path = nodePath;
    } catch { /* Path2D not fully supported (Happy-DOM) */ }

    // Dim dirty cash node when $0
    const isDimmed = node.role === 'dirty' && data.dirtyCash <= 0;
    const globalAlpha = isDimmed ? 0.25 : 1;
    ctx.globalAlpha = globalAlpha;

    // Glow
    ctx.shadowColor = node.glowColor;
    ctx.shadowBlur = isDimmed ? 4 : 12;
    roundRect(ctx, node.x, node.y, node.w, node.h, nodeR);
    ctx.fillStyle = 'rgba(13,13,26,0.9)';
    ctx.fill();
    ctx.strokeStyle = node.color + '66';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.shadowBlur = 0;

    // Dirty cash pulse
    if (node.role === 'dirty' && data.dirtyCash > 0) {
      const pulse = 0.3 + Math.abs(Math.sin(timestamp / 500)) * 0.7;
      ctx.shadowColor = `rgba(231,76,60,${pulse})`;
      ctx.shadowBlur = 16;
      roundRect(ctx, node.x, node.y, node.w, node.h, nodeR);
      ctx.strokeStyle = `rgba(231,76,60,${pulse * 0.6})`;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.shadowBlur = 0;
    }

    // Node icon (top-left, 14×14) — only for the 3 "main" nodes
    // (Revenue / Department / Audit). Clean + Dirty are end-of-flow
    // nodes with centered value layout and no icon.
    const iconByRole: Record<PipelineRole, typeof pathRevenue | null> = {
      revenue: pathRevenue,
      department: pathDepartment,
      audit: pathAudit,
      clean: null,
      dirty: null,
    };
    const iconFn = iconByRole[node.role];
    const hasIcon = iconFn !== null;
    if (iconFn) {
      const iconPath = iconFn(node.x + 6, node.y + 5);
      ctx.fillStyle = node.color;
      ctx.globalAlpha = isDimmed ? 0.25 : 0.7;
      ctx.fill(iconPath);
      ctx.globalAlpha = isDimmed ? 0.25 : 1;
    }

    // Label (top) — shifted right for icon on main nodes
    ctx.font = '600 10px "Share Tech Mono", monospace';
    ctx.fillStyle = 'rgba(160,175,190,0.7)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    const labelX = hasIcon ? node.x + node.w / 2 + 8 : node.x + node.w / 2;
    ctx.fillText(node.label, labelX, node.y + 8);

    // Value — end-nodes (Clean / Dirty) center vertically; main nodes
    // keep the value under the label with room for extras below.
    ctx.font = '600 18px "Share Tech Mono", monospace';
    ctx.fillStyle = node.color;
    ctx.textBaseline = 'middle';
    const isEndNode = node.role === 'clean' || node.role === 'dirty';
    const valY = isEndNode ? node.y + node.h / 2 + 4 : node.y + 36;
    ctx.fillText(node.value, node.x + node.w / 2, valY);

    // Revenue node — show top income lines
    if (node.role === 'revenue') {
      const top3 = [...data.incomeLines].sort((a, b) => b.amount - a.amount).slice(0, 3);
      ctx.font = '10px "Share Tech Mono", monospace';
      ctx.textAlign = 'left';
      top3.forEach((line, li) => {
        const ly = node.y + 52 + li * 14;
        const catColor = CATEGORY_COLORS[line.key] ?? (line.legal ? '#06d6a0' : '#f4a261');
        ctx.beginPath();
        ctx.arc(node.x + 10, ly, 3, 0, Math.PI * 2);
        ctx.fillStyle = catColor;
        ctx.fill();
        ctx.fillStyle = 'rgba(180,190,200,0.8)';
        ctx.fillText(balanceCategoryLabel(line), node.x + 18, ly);
        ctx.textAlign = 'right';
        ctx.fillStyle = '#2ecc71';
        ctx.fillText('+' + formatCash(Math.round(line.amount)), node.x + node.w - 6, ly);
        ctx.textAlign = 'left';
      });
    }

    // Department node — sub names (never reached with empty subs now
    // because the node itself is skipped; guard retained for safety).
    if (node.role === 'department') {
      ctx.font = '10px "Share Tech Mono", monospace';
      ctx.textAlign = 'center';
      if (data.subNames.length === 0) {
        // Empty state — dashed outline box + "NO SUBS" text
        const emptyX = node.x + 12, emptyY = node.y + 48;
        const emptyW = node.w - 24, emptyH = 36;
        ctx.setLineDash([4, 3]);
        ctx.strokeStyle = 'rgba(252,163,17,0.25)';
        ctx.lineWidth = 1;
        roundRect(ctx, emptyX, emptyY, emptyW, emptyH, 4);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = 'rgba(160,175,190,0.35)';
        ctx.font = '9px "Share Tech Mono", monospace';
        ctx.fillText('NO SUBS', node.x + node.w / 2, emptyY + emptyH / 2);
      } else {
        const visibleNames = data.subNames.slice(0, 3);
        visibleNames.forEach((name, si) => {
          const sy = node.y + 52 + si * 16;
          const chipW = Math.min(node.w - 16, ctx.measureText(name).width + 12);
          const chipX = node.x + (node.w - chipW) / 2;
          roundRect(ctx, chipX, sy - 2, chipW, 14, 3);
          ctx.fillStyle = 'rgba(252,163,17,0.15)';
          ctx.fill();
          ctx.strokeStyle = 'rgba(252,163,17,0.3)';
          ctx.lineWidth = 0.5;
          ctx.stroke();
          ctx.fillStyle = '#fca311';
          ctx.fillText(name, node.x + node.w / 2, sy + 4);
        });
        if (data.subNames.length > 3) {
          ctx.fillStyle = 'rgba(160,175,190,0.5)';
          ctx.fillText(`+${data.subNames.length - 3}`, node.x + node.w / 2, node.y + 52 + 3 * 16 + 4);
        }
      }
    }

    // Audit node — federal heat with heat-map color
    if (node.role === 'audit' && data.federalHeat > 0) {
      ctx.font = '10px "Share Tech Mono", monospace';
      ctx.textAlign = 'center';
      ctx.fillStyle = heatColor;
      ctx.fillText(`Federal Heat: ${Math.round(data.federalHeat)}`, node.x + node.w / 2, node.y + 60);
      // Heat bar below text
      const barY = node.y + 72, barW = node.w - 20, barH = 4;
      const barX = node.x + 10;
      roundRect(ctx, barX, barY, barW, barH, 2);
      ctx.fillStyle = 'rgba(60,70,80,0.5)';
      ctx.fill();
      const filledW = barW * (data.federalHeat / 100);
      if (filledW > 0) {
        roundRect(ctx, barX, barY, filledW, barH, 2);
        ctx.fillStyle = heatColor;
        ctx.fill();
      }
    }

    // Hover highlight
    if (i === hoveredNode) {
      ctx.save();
      ctx.shadowColor = node.color;
      ctx.shadowBlur = 20;
      roundRect(ctx, node.x - 1, node.y - 1, node.w + 2, node.h + 2, nodeR);
      ctx.strokeStyle = node.color;
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.restore();
    }

    ctx.globalAlpha = 1;
  });

  return nodes;
}

// ── Balance Bars (HTML) ───────────────────────────────────────────────

// Balance-Bars v2 (v1.10.1 DTO)
//
// Renders income + expense BalanceCategory arrays with optional
// subsystem-bucket grouping. Details are shown via click-to-expand.
// Each row is click-able where the detail has a targetNodeId or
// actionCommand (wired in Phase 4 — tooltip-only for now).
//
// Buckets: categories share a subsystem string ("passive" | "active" |
// "other"). When two or more buckets are present, render collapsed
// bucket headers; single-bucket lists skip the bucket level for density.

function buildBalanceBarsHTML(
  incomeLines: BalanceCategory[],
  expenseLines: BalanceCategory[],
  dailyIncome: number,
): string {
  const all = [...incomeLines, ...expenseLines];
  const maxAmount = Math.max(...all.map(l => l.amount), 1);

  function renderRow(line: BalanceCategory, type: 'income' | 'expense'): string {
    const pct = Math.max(1, Math.round((line.amount / maxAmount) * 100));
    // Dirty-lines get an amber accent to visually separate criminal proceeds
    // from legal flow even when both sit side-by-side in the list.
    const color = type === 'income'
      ? (line.legal ? 'var(--green)' : 'var(--amber)')
      : 'var(--red)';
    const sign = type === 'income' ? '+' : '\u2212';
    const hasDetails = !!(line.details && line.details.length > 0);
    const ratio = dailyIncome > 0 ? line.amount / dailyIncome : 0;
    let warnTag = '';
    if (type === 'expense' && ratio > EXPENSE_CRIT_PCT) warnTag = '<span class="eco-bal-warn eco-bal-warn-crit">KRITISCH</span>';
    else if (type === 'expense' && ratio > EXPENSE_WARN_PCT) warnTag = '<span class="eco-bal-warn eco-bal-warn-high">HOCH</span>';

    const icon = iconForCategory(line.key);
    const dirtyChip = !line.legal ? '<span class="eco-bal-dirty-chip" title="Dirty / nicht-legal">D</span>' : '';

    let detailsHTML = '';
    if (hasDetails) {
      const detailMaxAmount = Math.max(...line.details!.map(d => d.amount), 1);
      detailsHTML = `<div class="eco-bal-details" style="display:none">` +
        line.details!.map(d => {
          const dPct = Math.max(1, Math.round((d.amount / detailMaxAmount) * 100));
          const label = balanceDetailLabel(d);
          const count = d.count && d.count > 1 ? ` <span class="eco-bal-count">\u00d7${d.count}</span>` : '';
          const dataAttrs = [
            d.targetNodeId ? `data-target="${d.targetNodeId}"` : '',
            d.actionCommand ? `data-action="${d.actionCommand}"` : '',
          ].filter(Boolean).join(' ');
          return `<div class="eco-bal-detail-row" ${dataAttrs}>
            <span class="eco-bal-detail-label">${label}${count}</span>
            <div class="eco-bal-detail-bar-bg"><div class="eco-bal-detail-bar" style="width:${dPct}%;background:${color}"></div></div>
            <span class="eco-bal-detail-amount" style="color:${color}">${sign}${formatCash(Math.round(d.amount))}</span>
          </div>`;
        }).join('') +
        `</div>`;
    }

    return `<div class="eco-bal-row${hasDetails ? ' eco-bal-expandable' : ''}" data-has-details="${hasDetails}" data-key="${line.key}">
      <span class="eco-bal-icon">${icon}</span>
      <span class="eco-bal-label">${balanceCategoryLabel(line)}${dirtyChip}</span>
      <div class="eco-bal-bar-bg"><div class="eco-bal-bar" style="width:${pct}%;background:${color}"></div></div>
      <span class="eco-bal-spark" data-spark-key="${line.key}" style="color:${color}"></span>
      <span class="eco-bal-amount" style="color:${color}">${sign}${formatCash(Math.round(line.amount))}</span>
      ${warnTag}
      ${hasDetails ? '<span class="eco-bal-chevron">\u25B8</span>' : ''}
    </div>${detailsHTML}`;
  }

  // Group by subsystem, sort within by registry Order.
  function groupBySubsystem(lines: BalanceCategory[]): Record<string, BalanceCategory[]> {
    const g: Record<string, BalanceCategory[]> = {};
    for (const l of lines) {
      (g[l.subsystem] ??= []).push(l);
    }
    for (const key of Object.keys(g)) {
      g[key].sort((a, b) => a.order - b.order);
    }
    return g;
  }

  function renderBucketedSection(
    lines: BalanceCategory[],
    type: 'income' | 'expense',
  ): string {
    if (lines.length === 0) {
      return `<div class="eco-bal-empty">${t('economy.balance_empty') ?? '—'}</div>`;
    }
    const bucketed = groupBySubsystem(lines);
    const bucketKeys = ['passive', 'active', 'other'].filter(k => bucketed[k]);
    // If everything is in a single bucket, skip the bucket header for density.
    if (bucketKeys.length <= 1) {
      return lines.sort((a, b) => a.order - b.order).map(l => renderRow(l, type)).join('');
    }
    return bucketKeys.map(bk => {
      const bucketLabel = t(`economy.bucket.${bk}`);
      const total = bucketed[bk].reduce((s, l) => s + l.amount, 0);
      const sign = type === 'income' ? '+' : '\u2212';
      return `<div class="eco-bal-bucket" data-bucket="${bk}">
        <div class="eco-bal-bucket-head"><span class="eco-bal-bucket-name">${bucketLabel}</span><span class="eco-bal-bucket-total">${sign}${formatCash(Math.round(total))}</span><span class="eco-bal-bucket-chevron">\u25BE</span></div>
        <div class="eco-bal-bucket-body">
          ${bucketed[bk].map(l => renderRow(l, type)).join('')}
        </div>
      </div>`;
    }).join('');
  }

  return `<div class="eco-bal-section">
    <div class="eco-bal-header">${t('economy.income_header') ?? 'EINNAHMEN'}</div>
    ${renderBucketedSection(incomeLines, 'income')}
  </div>
  <div class="eco-bal-sep"></div>
  <div class="eco-bal-section">
    <div class="eco-bal-header">${t('economy.expenses_header') ?? 'AUSGABEN'}</div>
    ${renderBucketedSection(expenseLines, 'expense')}
  </div>`;
}

// ── Building Type Icons (SVG inline) ──────────────────────────────────

const BLDG_TYPE_ICONS: Record<string, string> = {
  'Logistik': '<svg viewBox="0 0 16 16" width="16" height="16"><path d="M1 12V6l5-3 5 3v6H8V9H4v3z" fill="none" stroke="currentColor" stroke-width="1.2"/><path d="M11 8h4l-2-2z" fill="currentColor" opacity="0.5"/><rect x="11" y="8" width="4" height="4" fill="none" stroke="currentColor" stroke-width="1"/></svg>',
  'Gesundheit': '<svg viewBox="0 0 16 16" width="16" height="16"><rect x="5" y="2" width="6" height="12" rx="1" fill="none" stroke="currentColor" stroke-width="1.2"/><rect x="2" y="5" width="12" height="6" rx="1" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>',
  'Bio & Chemie': '<svg viewBox="0 0 16 16" width="16" height="16"><path d="M5 2v4l-3 7a1 1 0 001 1h10a1 1 0 001-1l-3-7V2" fill="none" stroke="currentColor" stroke-width="1.2"/><line x1="4" y1="2" x2="12" y2="2" stroke="currentColor" stroke-width="1.2"/><circle cx="7" cy="10" r="1" fill="currentColor"/><circle cx="10" cy="11" r="0.7" fill="currentColor"/></svg>',
  'Retail (Tech)': '<svg viewBox="0 0 16 16" width="16" height="16"><rect x="2" y="4" width="12" height="9" rx="1" fill="none" stroke="currentColor" stroke-width="1.2"/><path d="M4 4V3a4 4 0 018 0v1" fill="none" stroke="currentColor" stroke-width="1.2"/><line x1="2" y1="10" x2="14" y2="10" stroke="currentColor" stroke-width="0.8" opacity="0.4"/></svg>',
};

function buildingTypeIcon(type: string): string {
  return BLDG_TYPE_ICONS[type] ?? '<svg viewBox="0 0 16 16" width="16" height="16"><rect x="3" y="3" width="10" height="10" rx="1" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>';
}

// ── Building Card HTML Generator ─────────────────────────────────────

function buildBuildingCard(b: import('../data/fixture').BuildingDTO, runners: RunnerDTO[], isOwned: boolean): string {
  const noMgr = !b.hasManager;
  const frontBadge = b.isFront ? '<span class="eco-bldg-badge eco-bldg-front">FRONT</span>' : '';
  const zoneBadge = b.zone ? `<span class="eco-bldg-badge eco-bldg-zone">${b.zone.toUpperCase()}</span>` : '';
  const heatColor = b.heat > 40 ? 'var(--red)' : b.heat > 20 ? 'var(--amber)' : 'var(--green)';
  const profit = (b.income ?? 0) - (b.rent ?? 0);
  const profitColor = profit >= 0 ? 'var(--green)' : 'var(--red)';
  const heatWarnClass = b.heat > 40 ? ' heat-warn' : '';
  const ownedClass = isOwned ? '' : ' not-owned';

  // Manager portrait
  let portraitHtml = '';
  if (isOwned && b.managerName) {
    const runner = runners.find(r => r.name === b.managerName);
    if (runner) {
      portraitHtml = `<span class="eco-bldg-portrait">${portraitHTML(runner, 32)}</span>`;
    }
  }

  // Synergy tags
  const synergyHtml = (b.synergyTags && b.synergyTags.length > 0)
    ? `<div class="eco-bldg-synergy">${b.synergyTags.map(tag => `<span class="eco-bldg-synergy-tag">${tag}</span>`).join('')}</div>`
    : '';

  // Storage capacity
  const storageHtml = (b.storageCap && b.storageCap > 0)
    ? `<div class="eco-bldg-storage">
        <div class="eco-bldg-storage-label">${t('economy.col_storage')}: ${Math.round(b.storageCap)}u</div>
        <div class="eco-bldg-storage-bar"><div style="width:0%;height:100%;background:var(--cyan);border-radius:2px"></div></div>
      </div>`
    : '';

  // Expandable details
  const detailsId = `bldg-detail-${b.nodeId.replace(/[^a-zA-Z0-9]/g, '_')}`;

  return `<div class="eco-bldg-card${heatWarnClass}${ownedClass}" data-profit="${profit}" data-heat="${b.heat}" data-zone="${b.zone ?? ''}" data-front="${b.isFront}" data-sector="${b.sector}">
    <div class="eco-bldg-header">
      <span class="eco-bldg-name"><span class="eco-bldg-type-icon" style="color:var(--text-lo)">${buildingTypeIcon(b.type)}</span>${b.name}</span>
      <div class="eco-bldg-badges">${frontBadge}${zoneBadge}${!isOwned ? '<span class="eco-bldg-badge" style="background:rgba(100,120,140,0.1);border:1px solid rgba(100,120,140,0.2);color:var(--text-lo)">' + t('economy.not_owned') + '</span>' : ''}</div>
    </div>
    <div class="eco-bldg-type">${b.type} &middot; ${b.sector}</div>
    <div class="eco-bldg-stats">
      <div class="eco-bldg-stat">
        <span class="eco-bldg-stat-label">${t('economy.col_income_day')}</span>
        <span style="color:${noMgr && isOwned ? 'var(--red)' : 'var(--green)'};font-family:var(--mono)">${noMgr && isOwned ? '$0' : '+' + formatCash(Math.round(b.income))}</span>
      </div>
      <div class="eco-bldg-stat">
        <span class="eco-bldg-stat-label">${t('economy.col_rent')}</span>
        <span style="color:var(--red);font-family:var(--mono)">${b.rent ? '\u2212' + formatCash(Math.round(b.rent)) : '\u2014'}</span>
      </div>
      <div class="eco-bldg-stat">
        <span class="eco-bldg-stat-label">${t('economy.col_profit')}</span>
        <span style="color:${profitColor};font-family:var(--mono)">${noMgr && isOwned ? '\u2014' : (profit >= 0 ? '+' : '\u2212') + formatCash(Math.round(Math.abs(profit)))}</span>
      </div>
      ${b.propertyValue ? `<div class="eco-bldg-stat">
        <span class="eco-bldg-stat-label">${t('economy.col_value')}</span>
        <span style="color:var(--amber);font-family:var(--mono)">${formatCash(Math.round(b.propertyValue))}</span>
      </div>` : ''}
    </div>
    <div class="eco-bldg-footer">
      <div class="eco-bldg-mgr" style="color:${noMgr && isOwned ? 'var(--red)' : 'var(--text-mid)'}">
        ${isOwned
          ? (noMgr
            ? '<span style="color:var(--red)">\u26A0 ' + t('economy.no_manager') + '</span>'
            : portraitHtml + ' ' + (b.managerName || 'Manager'))
          : '<span style="color:var(--text-lo)">\u2014</span>'}
      </div>
      <div style="display:flex;align-items:center;gap:8px">
        <div style="display:flex;align-items:center;gap:4px">
          <span style="font-size:10px;color:var(--text-lo)">HEAT</span>
          <div style="width:40px;height:4px;background:var(--border);border-radius:2px">
            <div style="width:${Math.min(100, Math.round(b.heat))}%;height:100%;background:${heatColor};border-radius:2px"></div>
          </div>
          <span style="font-family:var(--mono);font-size:10px;color:${heatColor}">${Math.round(b.heat)}</span>
        </div>
        ${b.defense ? `<span style="font-size:10px;color:var(--text-lo)">DEF <span style="font-family:var(--mono);color:var(--cyan-dim)">${b.defense}</span></span>` : ''}
      </div>
    </div>
    <!-- Expandable details -->
    <div class="eco-bldg-details collapsed" id="${detailsId}">
      ${synergyHtml}${storageHtml}
    </div>
    ${(synergyHtml || storageHtml) ? `<button class="eco-bldg-expand" data-target="${detailsId}">${t('economy.expand_details')} \u25B8</button>` : ''}
  </div>`;
}

// ── Buy Building Modal ─────────────────────────────────────────────────

function openBuyBuildingModal(runners: RunnerDTO[]): void {
  // Get available buildings from mapData (not yet owned by player)
  const ownedIds = new Set(fixture.player.buildings.filter(b => b.owned).map(b => b.nodeId));
  const available = (mapData?.worldBuildings ?? [])
    .filter(wb => wb.rentable && !ownedIds.has(wb.node_id))
    .sort((a, b) => a.buy_price - b.buy_price);

  if (available.length === 0) {
    alert(t('economy.buy_no_buildings'));
    return;
  }

  const cleanCash = state.cleanCash;

  // Group by sector
  const bySector = new Map<string, typeof available>();
  for (const wb of available) {
    const arr = bySector.get(wb.sector_id) ?? [];
    arr.push(wb);
    bySector.set(wb.sector_id, arr);
  }
  const sectors = Array.from(bySector.keys()).sort();
  // A building is "actionable" when the player can afford either to buy it or
  // (the cheaper option) rent it. The list is pre-filtered to `rentable`, so
  // renting is always an available path.
  const canAfford = (wb: typeof available[0]) => cleanCash >= Math.min(wb.buy_price, wb.rent);
  const affordableCount = available.filter(canAfford).length;

  // Build building item HTML
  function buildItemHTML(wb: typeof available[0]): string {
    const canBuy = cleanCash >= wb.buy_price;
    const canRent = cleanCash >= wb.rent;
    const actionable = canBuy || canRent;
    return `<div class="eco-buy-item${actionable ? '' : ' unaffordable-item'}" data-node-id="${wb.node_id}" data-sector="${wb.sector_id}" data-affordable="${actionable}">
      <div class="eco-buy-item-main">
        <span class="eco-buy-item-name">${buildingTypeIcon(wb.category)} ${escHtml(wb.display_name)}</span>
        <span class="eco-buy-item-price${canBuy ? '' : ' unaffordable'}">${formatCash(Math.round(wb.buy_price))}</span>
      </div>
      <div class="eco-buy-item-details">
        <span>${wb.category} \u00B7 ${wb.zone}</span>
        <span>${t('economy.col_income_day')}: <span style="color:var(--green);font-family:var(--mono)">+${formatCash(Math.round(wb.income_base))}</span></span>
        <span>${t('economy.col_rent')}: <span style="color:${canRent ? 'var(--amber)' : 'var(--red)'};font-family:var(--mono)">${wb.rent ? formatCash(Math.round(wb.rent)) + t('economy.buy_per_day') : '\u2014'}</span></span>
        <span>DEF: <span style="font-family:var(--mono)">${wb.defense_value}</span></span>
        ${wb.is_front_company ? '<span style="color:var(--amber)">FRONT</span>' : ''}
      </div>
    </div>`;
  }

  // Create overlay
  const overlay = document.createElement('div');
  overlay.className = 'eco-buy-overlay overlay';
  overlay.innerHTML = `
    <div class="eco-buy-modal">
      <div class="eco-buy-header">
        <span class="eco-buy-title">${t('economy.buy_title')}</span>
        <button class="eco-buy-close btn-icon" style="font-size:18px">\u2715</button>
      </div>
      <div class="eco-buy-budget">
        ${t('economy.buy_budget')}: <span style="color:var(--green);font-family:var(--mono)">${formatCash(Math.round(cleanCash))}</span>
        <span style="font-size:11px;color:var(--text-lo);margin-left:8px">(${t('economy.buy_clean_only')})</span>
      </div>
      <!-- Filters -->
      <div class="eco-buy-filters">
        <div class="eco-buy-filter-row">
          <button class="eco-buy-sector-btn btn sm active" data-sector="all">${t('economy.filter_all')} (${affordableCount})</button>
          ${sectors.map(s => {
            const cnt = (bySector.get(s) ?? []).filter(wb => cleanCash >= wb.buy_price).length;
            return `<button class="eco-buy-sector-btn btn sm" data-sector="${s}">${s} (${cnt})</button>`;
          }).join('')}
        </div>
        <label class="eco-buy-toggle">
          <input type="checkbox" id="eco-buy-show-unaffordable">
          <span>${t('economy.buy_show_unaffordable')}</span>
        </label>
      </div>
      <div class="eco-buy-list" id="eco-buy-list">
        ${sectors.map(s => {
          const sectorBuildings = bySector.get(s) ?? [];
          return `<div class="eco-buy-sector-group" data-sector-group="${s}">
            <div class="eco-buy-sector-label">${t('economy.section_sector', { name: s })} (${sectorBuildings.length})</div>
            ${sectorBuildings.map(wb => buildItemHTML(wb)).join('')}
          </div>`;
        }).join('')}
      </div>
      <!-- Runner selection (hidden until building selected) -->
      <div class="eco-buy-runner-section" id="eco-buy-runner-section" style="display:none">
        <div class="eco-buy-subtitle">${t('economy.buy_select_runner')}</div>
        <div class="eco-buy-runner-list" id="eco-buy-runner-list">
          ${runners.map(r => `<div class="eco-buy-runner" data-runner-id="${r.id}">
            <span>${escHtml(r.name)}</span>
            <span style="font-size:11px;color:var(--text-lo)">${escHtml(r.skills?.join(', ') ?? '')}</span>
          </div>`).join('')}
        </div>
      </div>
      <div class="eco-buy-summary" id="eco-buy-summary" style="display:none"></div>
      <div class="eco-buy-actions" id="eco-buy-actions" style="display:none">
        <button class="btn sm" id="eco-buy-cancel-step">${t('economy.buy_back')}</button>
        <div class="eco-buy-acquire">
          <button class="btn sm amber" id="eco-buy-rent" disabled>${t('economy.buy_mode_rent')}</button>
          <button class="eco-buy-confirm" id="eco-buy-confirm" disabled>${t('economy.buy_confirm')}</button>
        </div>
      </div>
      <div class="eco-buy-status" id="eco-buy-status"></div>
    </div>
  `;

  // Style
  const style = document.createElement('style');
  style.textContent = `
    /* .eco-buy-overlay → shared .overlay (marker kept for tests) */
    .eco-buy-modal { background:var(--surface-modal); backdrop-filter:blur(4px); -webkit-backdrop-filter:blur(4px); border:1px solid var(--line-hi); border-radius:8px; width:600px; max-width:90vw; max-height:85vh; display:flex; flex-direction:column; }
    .eco-buy-header { display:flex; justify-content:space-between; align-items:center; padding:16px 20px; border-bottom:1px solid var(--border); }
    .eco-buy-title { font-family:var(--mono); font-size:14px; text-transform:uppercase; letter-spacing:2px; color:var(--cyan); }
    /* .eco-buy-close → shared .btn-icon */
    .eco-buy-budget { padding:12px 20px; font-size:13px; color:var(--text-mid); border-bottom:1px solid var(--border); }
    .eco-buy-filters { padding:8px 16px; border-bottom:1px solid var(--border); display:flex; flex-direction:column; gap:6px; }
    .eco-buy-filter-row { display:flex; gap:4px; flex-wrap:wrap; }
    /* .eco-buy-sector-btn → shared .btn.sm (+ .active); marker kept for JS filter toggle */
    .eco-buy-toggle { display:flex; align-items:center; gap:6px; font-size:11px; color:var(--text-lo); cursor:pointer; }
    .eco-buy-toggle input { accent-color:var(--cyan); }
    .eco-buy-list { overflow-y:auto; flex:1; min-height:0; padding:8px 12px; }
    .eco-buy-sector-group { margin-bottom:8px; }
    .eco-buy-sector-label { font-family:var(--mono); font-size:10px; text-transform:uppercase; letter-spacing:1px; color:var(--text-lo); padding:6px 4px 4px; border-bottom:1px solid var(--border); margin-bottom:6px; }
    .eco-buy-item { padding:10px 12px; border:1px solid var(--border); border-radius:6px; margin-bottom:6px; cursor:pointer; transition:border-color 0.15s; }
    .eco-buy-item:hover { border-color:var(--cyan); }
    .eco-buy-item.selected { border-color:var(--cyan); background:rgba(var(--ac),0.05); }
    .eco-buy-item.unaffordable-item { opacity:0.4; cursor:not-allowed; display:none; }
    .eco-buy-item.unaffordable-item.show { display:block; }
    .eco-buy-item-main { display:flex; justify-content:space-between; align-items:center; }
    .eco-buy-item-name { font-size:13px; color:var(--text-hi); display:flex; align-items:center; gap:6px; }
    .eco-buy-item-price { font-family:var(--mono); font-size:13px; color:var(--amber); }
    .eco-buy-item-price.unaffordable { color:var(--red); }
    .eco-buy-item-details { display:flex; gap:12px; font-size:11px; color:var(--text-lo); margin-top:4px; flex-wrap:wrap; }
    .eco-buy-runner-section { padding:8px 12px; border-top:1px solid var(--border); }
    .eco-buy-subtitle { font-family:var(--mono); font-size:11px; text-transform:uppercase; letter-spacing:1px; color:var(--text-lo); margin-bottom:8px; }
    .eco-buy-runner-list { display:flex; flex-direction:column; gap:4px; max-height:20vh; overflow-y:auto; }
    .eco-buy-runner { padding:8px 12px; border:1px solid var(--border); border-radius:4px; cursor:pointer; display:flex; justify-content:space-between; align-items:center; transition:border-color 0.15s; }
    .eco-buy-runner:hover { border-color:var(--cyan); }
    .eco-buy-runner.selected { border-color:var(--cyan); background:rgba(var(--ac),0.05); }
    .eco-buy-actions { padding:12px 20px; border-top:1px solid var(--border); display:flex; gap:8px; justify-content:space-between; align-items:center; }
    .eco-buy-acquire { display:flex; gap:8px; }
    .eco-buy-confirm { padding:8px 20px; background:var(--cyan); color:var(--bg); border:none; border-radius:4px; font-family:var(--mono); font-size:12px; text-transform:uppercase; cursor:pointer; }
    .eco-buy-confirm:disabled { opacity:0.4; cursor:not-allowed; }
    .eco-buy-confirm:hover:not(:disabled) { filter:brightness(1.1); }
    /* .eco-buy-rent → shared .btn.amber · .eco-buy-cancel → shared .btn */
    .eco-buy-summary { padding:10px 20px; border-top:1px solid var(--border); }
    .eco-buy-summary-head { font-size:13px; color:var(--text-hi); margin-bottom:4px; }
    .eco-buy-summary-opts { display:flex; gap:16px; flex-wrap:wrap; font-family:var(--mono); font-size:11px; color:var(--text-lo); }
    .eco-buy-status { padding:8px 20px; font-size:12px; min-height:20px; }
  `;
  overlay.appendChild(style);
  document.body.appendChild(overlay);

  let selectedWb: typeof available[0] | null = null;
  let selectedRunner: string | null = null;
  let activeSector = 'all';

  // Apply filters: sector + affordability
  function applyFilters(): void {
    const showUnaffordable = (overlay.querySelector('#eco-buy-show-unaffordable') as HTMLInputElement).checked;
    // Sector groups
    overlay.querySelectorAll('.eco-buy-sector-group').forEach(grp => {
      const s = (grp as HTMLElement).dataset.sectorGroup!;
      (grp as HTMLElement).style.display = (activeSector === 'all' || activeSector === s) ? '' : 'none';
    });
    // Unaffordable items
    overlay.querySelectorAll('.eco-buy-item.unaffordable-item').forEach(el => {
      el.classList.toggle('show', showUnaffordable);
    });
  }

  // Enable Buy / Rent independently, per the selected building's affordability
  // and whether a runner has been chosen. Rent is hidden for rent-free buildings.
  function updateActionButtons(): void {
    const rentBtn = overlay.querySelector('#eco-buy-rent') as HTMLButtonElement;
    const buyBtn = overlay.querySelector('#eco-buy-confirm') as HTMLButtonElement;
    const hasRunner = !!selectedRunner;
    rentBtn.disabled = !(hasRunner && !!selectedWb && selectedWb.rent > 0 && cleanCash >= selectedWb.rent);
    buyBtn.disabled = !(hasRunner && !!selectedWb && cleanCash >= selectedWb.buy_price);
  }

  // Confirm summary: building, chosen runner, and resulting clean cash per option.
  function updateSummary(): void {
    const summaryEl = overlay.querySelector('#eco-buy-summary') as HTMLElement;
    if (!selectedWb) { summaryEl.style.display = 'none'; return; }
    const runner = runners.find(r => r.id === selectedRunner);
    const after = (cost: number) => formatCash(Math.round(cleanCash - cost));
    summaryEl.style.display = '';
    summaryEl.innerHTML = `
      <div class="eco-buy-summary-head">${buildingTypeIcon(selectedWb.category)} ${escHtml(selectedWb.display_name)}${runner ? ` <span style="color:var(--text-lo)">→ ${escHtml(runner.name)}</span>` : ''}</div>
      <div class="eco-buy-summary-opts">
        <span>${t('economy.buy_confirm')}: ${formatCash(Math.round(selectedWb.buy_price))} → ${after(selectedWb.buy_price)}</span>
        ${selectedWb.rent > 0 ? `<span>${t('economy.buy_mode_rent')}: ${formatCash(Math.round(selectedWb.rent))}${t('economy.buy_per_day')} → ${after(selectedWb.rent)}</span>` : ''}
      </div>`;
  }

  // Sector filter buttons
  overlay.querySelectorAll('.eco-buy-sector-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      overlay.querySelectorAll('.eco-buy-sector-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      activeSector = (btn as HTMLElement).dataset.sector!;
      applyFilters();
    });
  });

  // Unaffordable toggle
  overlay.querySelector('#eco-buy-show-unaffordable')?.addEventListener('change', () => applyFilters());

  // Close
  const close = () => overlay.remove();
  overlay.querySelector('.eco-buy-close')!.addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  // Building selection (only actionable items — affordable to buy or rent)
  overlay.querySelectorAll('.eco-buy-item:not(.unaffordable-item)').forEach(item => {
    item.addEventListener('click', () => {
      overlay.querySelectorAll('.eco-buy-item').forEach(i => i.classList.remove('selected'));
      item.classList.add('selected');
      const nodeId = (item as HTMLElement).dataset.nodeId!;
      selectedWb = available.find(wb => wb.node_id === nodeId) ?? null;
      selectedRunner = null;
      overlay.querySelectorAll('.eco-buy-runner').forEach(r => r.classList.remove('selected'));

      // Price-tag the action buttons; hide Rent for rent-free buildings.
      const rentBtn = overlay.querySelector('#eco-buy-rent') as HTMLButtonElement;
      const buyBtn = overlay.querySelector('#eco-buy-confirm') as HTMLButtonElement;
      if (selectedWb) {
        buyBtn.textContent = `${t('economy.buy_confirm')} · ${formatCash(Math.round(selectedWb.buy_price))}`;
        rentBtn.textContent = `${t('economy.buy_mode_rent')} · ${formatCash(Math.round(selectedWb.rent))}${t('economy.buy_per_day')}`;
        rentBtn.style.display = selectedWb.rent > 0 ? '' : 'none';
      }

      // Reveal runner picker + summary + actions
      (overlay.querySelector('#eco-buy-runner-section') as HTMLElement).style.display = '';
      (overlay.querySelector('#eco-buy-actions') as HTMLElement).style.display = '';
      updateSummary();
      updateActionButtons();
    });
  });

  // Runner selection
  overlay.querySelectorAll('.eco-buy-runner').forEach(item => {
    item.addEventListener('click', () => {
      overlay.querySelectorAll('.eco-buy-runner').forEach(r => r.classList.remove('selected'));
      item.classList.add('selected');
      selectedRunner = (item as HTMLElement).dataset.runnerId!;
      updateSummary();
      updateActionButtons();
    });
  });

  // Back button
  overlay.querySelector('#eco-buy-cancel-step')?.addEventListener('click', () => {
    selectedWb = null;
    selectedRunner = null;
    overlay.querySelectorAll('.eco-buy-item').forEach(i => i.classList.remove('selected'));
    overlay.querySelectorAll('.eco-buy-runner').forEach(r => r.classList.remove('selected'));
    (overlay.querySelector('#eco-buy-runner-section') as HTMLElement).style.display = 'none';
    (overlay.querySelector('#eco-buy-summary') as HTMLElement).style.display = 'none';
    (overlay.querySelector('#eco-buy-actions') as HTMLElement).style.display = 'none';
  });

  // Acquire — Buy or Rent share the same payload shape, only the command differs.
  async function submitAcquire(command: 'BUY_BUILDING' | 'RENT_BUILDING'): Promise<void> {
    const wb = selectedWb;
    const runnerId = selectedRunner;
    const sessionId = state.sessionId;
    if (!wb || !runnerId || !sessionId) return;

    const statusEl = overlay.querySelector('#eco-buy-status') as HTMLElement;
    const rentBtn = overlay.querySelector('#eco-buy-rent') as HTMLButtonElement;
    const buyBtn = overlay.querySelector('#eco-buy-confirm') as HTMLButtonElement;
    rentBtn.disabled = true;
    buyBtn.disabled = true;
    statusEl.textContent = t('economy.buy_submitting');
    statusEl.style.color = 'var(--text-mid)';

    try {
      const result = await submitPlan(sessionId, {
        hood_id: runnerId,
        actions: [{ runner: runnerId, command, target: wb.node_id }],
      });
      if (result.ok) {
        statusEl.textContent = t('economy.buy_success');
        statusEl.style.color = 'var(--green)';
        setTimeout(close, 1500);
      } else {
        statusEl.textContent = result.errors?.join(', ') || t('economy.buy_failed');
        statusEl.style.color = 'var(--red)';
        updateActionButtons();
      }
    } catch (err) {
      statusEl.textContent = String((err as Error).message ?? t('economy.buy_failed'));
      statusEl.style.color = 'var(--red)';
      updateActionButtons();
    }
  }

  overlay.querySelector('#eco-buy-confirm')?.addEventListener('click', () => submitAcquire('BUY_BUILDING'));
  overlay.querySelector('#eco-buy-rent')?.addEventListener('click', () => submitAcquire('RENT_BUILDING'));
}

// ── Main Render ────────────────────────────────────────────────────────

// ── Phase 3 deep-dive tab renderers ──────────────────────────────────────
//
// Each renders into a container div supplied by the overview-HTML template.
// All reuse the DTO v2 BalanceCategory shape + the history cache so the
// tabs stay in sync with the Übersicht.

/** Renders a card per legal/dirty category with trend sparkline (if
 *  history available). Category container takes `lines` filtered by
 *  legal-ness. Card body: label, current amount, per-detail sub-rows. */
function renderFlowCards(container: HTMLElement, lines: BalanceCategory[], color: string): void {
  if (lines.length === 0) {
    container.innerHTML = `<div style="padding:20px;text-align:center;color:var(--text-lo);font-style:italic">${t('economy.flow_empty') ?? 'Keine Einträge'}</div>`;
    return;
  }
  container.innerHTML = lines.sort((a, b) => b.amount - a.amount).map(line => {
    const detailsHTML = (line.details ?? []).map(d => {
      const count = d.count && d.count > 1 ? ` <span style="color:var(--text-lo)">\u00d7${d.count}</span>` : '';
      return `<div class="eco-flow-detail-row">
        <span>${balanceDetailLabel(d)}${count}</span>
        <span style="color:${color};font-family:var(--mono)">${formatCash(Math.round(d.amount))}</span>
      </div>`;
    }).join('');
    return `<div class="eco-flow-card">
      <div class="eco-flow-card-head">
        <span class="eco-flow-card-title">${balanceCategoryLabel(line)}</span>
        <span class="eco-flow-card-amount" style="color:${color}">${formatCash(Math.round(line.amount))}</span>
      </div>
      ${detailsHTML ? `<div class="eco-flow-card-details">${detailsHTML}</div>` : ''}
    </div>`;
  }).join('');
}

/** Draws a stacked-history chart for the given field across snapshots.
 *  X-axis = day, Y-axis = amount. Two lines so caller can show e.g. both
 *  legal and dirty on one chart. */
function drawHistoryChart(
  canvas: HTMLCanvasElement,
  snaps: EconomySnapshot[],
  series: Array<{ label: string; color: string; get: (s: EconomySnapshot) => number }>,
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx || snaps.length < 2) {
    if (ctx) {
      ctx.fillStyle = 'rgba(180,190,200,0.4)';
      ctx.font = '12px "Share Tech Mono", monospace';
      ctx.textAlign = 'center';
      ctx.fillText(t('economy.history_insufficient') ?? 'Keine ausreichenden Daten (min. 2 Zyklen)', canvas.width / 2, canvas.height / 2);
    }
    return;
  }
  const dpr = window.devicePixelRatio || 1;
  canvas.width = canvas.clientWidth * dpr;
  canvas.height = canvas.clientHeight * dpr;
  ctx.scale(dpr, dpr);
  const W = canvas.clientWidth;
  const H = canvas.clientHeight;
  const pad = 30;

  ctx.clearRect(0, 0, W, H);

  // Find max across all series for Y-axis scaling
  let maxVal = 0;
  for (const s of series) {
    for (const snap of snaps) {
      maxVal = Math.max(maxVal, s.get(snap));
    }
  }
  if (maxVal <= 0) maxVal = 1;

  // Axes
  ctx.strokeStyle = 'rgba(180,190,200,0.15)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(pad, pad);
  ctx.lineTo(pad, H - pad);
  ctx.lineTo(W - pad, H - pad);
  ctx.stroke();

  // Series lines
  for (const s of series) {
    ctx.strokeStyle = s.color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    snaps.forEach((snap, i) => {
      const x = pad + (i / (snaps.length - 1)) * (W - 2 * pad);
      const y = H - pad - (s.get(snap) / maxVal) * (H - 2 * pad);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
  }

  // Legend
  ctx.font = '11px "Share Tech Mono", monospace';
  ctx.textAlign = 'left';
  series.forEach((s, i) => {
    const y = pad + 4 + i * 14;
    ctx.fillStyle = s.color;
    ctx.fillRect(W - pad - 80, y - 4, 8, 8);
    ctx.fillStyle = 'rgba(180,190,200,0.85)';
    ctx.fillText(s.label, W - pad - 68, y + 4);
  });

  // X-axis day labels
  ctx.fillStyle = 'rgba(180,190,200,0.5)';
  ctx.font = '9px "Share Tech Mono", monospace';
  ctx.textAlign = 'center';
  ctx.fillText(`d${snaps[0].day}`, pad, H - pad + 14);
  ctx.fillText(`d${snaps[snaps.length - 1].day}`, W - pad, H - pad + 14);
}

export function renderEconomy(): HTMLElement {
  const buildings = fixture.player.buildings.filter(b => b.owned);
  const subs      = fixture.player.subs;
  const vehicles  = fixture.player.vehicles;
  const p         = fixture.player;

  const root = document.createElement('div');
  root.className = 'screen';

  const style = document.createElement('style');
  style.textContent = `
    .eco-tabs { display:flex; gap:2px; margin-bottom:16px; }
    .eco-tab { padding:8px 16px; background:none; border:none; border-bottom:2px solid transparent; color:var(--text-lo); font-family:var(--display); font-size:11px; font-weight:700; cursor:pointer; text-transform:uppercase; letter-spacing:1.5px; transition:all 0.12s; }
    .eco-tab.active { color:var(--cyan); border-bottom-color:var(--cyan); text-shadow:0 0 8px rgba(var(--ac),0.4); }
    .eco-table { width:100%; border-collapse:collapse; font-size:13px; }
    .eco-table th { text-align:left; padding:6px 10px; font-family:var(--display); font-size:9px; font-weight:700; letter-spacing:1.5px; text-transform:uppercase; color:var(--text-lo); border-bottom:1px solid var(--line); }
    .eco-table td { padding:7px 10px; border-bottom:1px solid rgba(255,255,255,0.04); }
    .eco-table tr:hover td { background:var(--bg-hover); }
    .tier-badge { font-family:var(--mono); font-size:11px; padding:2px 6px; border-radius:3px; }
    .tier-micro    { background:rgba(52,152,219,0.15); border:1px solid rgba(52,152,219,0.3); color:var(--blue); }
    .tier-standard { background:rgba(var(--ac),0.1); border:1px solid rgba(var(--ac),0.25); color:var(--cyan-dim); }
    .tier-shell    { background:rgba(247,37,133,0.1); border:1px solid rgba(247,37,133,0.25); color:var(--magenta); }

    /* ── KPI Cards ─────────────────────────────────────────── */
    .eco-kpi-row { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-bottom:16px; }
    .eco-kpi-card { position:relative; background:var(--bg-tile); border:1px solid var(--line); padding:16px 12px; text-align:center; clip-path:polygon(0 0,calc(100% - 9px) 0,100% 9px,100% 100%,9px 100%,0 calc(100% - 9px)); }
    .eco-kpi-icon { margin-bottom:6px; color:var(--text-lo); }
    .eco-kpi-value { font-family:var(--mono); font-size:24px; line-height:1.2; }
    .eco-kpi-label { font-family:var(--sans); font-size:11px; text-transform:uppercase; letter-spacing:1.5px; color:var(--text-lo); margin-top:4px; }
    .eco-kpi-trend { font-family:var(--mono); font-size:11px; margin-top:4px; }
    .eco-kpi-trend-up { color:var(--green); }
    .eco-kpi-trend-down { color:var(--red); }
    .eco-kpi-spark { margin-top:8px; }
    .eco-kpi-split { display:flex; justify-content:center; gap:10px; margin-top:4px; font-family:var(--mono); font-size:10px; letter-spacing:0.05em; }
    .eco-kpi-diff { font-family:var(--mono); font-size:10px; margin-top:4px; min-height:12px; }
    .eco-flow-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(280px, 1fr)); gap:12px; }
    .eco-flow-card { background:var(--surface-frost); backdrop-filter:blur(3px); -webkit-backdrop-filter:blur(3px); border:1px solid var(--line); border-radius:3px; padding:10px 12px; }
    .eco-flow-card-head { display:flex; justify-content:space-between; align-items:baseline; margin-bottom:6px; }
    .eco-flow-card-title { color:var(--text-hi); font-weight:600; font-size:13px; }
    .eco-flow-card-amount { font-family:var(--mono); font-size:18px; }
    .eco-flow-card-details { border-top:1px solid var(--border); padding-top:6px; font-size:11px; color:var(--text-mid); }
    .eco-flow-detail-row { display:flex; justify-content:space-between; padding:2px 0; }
    .eco-launder-row { display:flex; justify-content:space-between; padding:6px 8px; background:rgba(255,255,255,0.02); border:1px solid var(--border); border-radius:3px; margin-bottom:4px; font-family:var(--mono); font-size:12px; }
    .eco-launder-bar { height:4px; background:rgba(255,255,255,0.05); border-radius:2px; margin-top:4px; overflow:hidden; }
    .eco-launder-bar-fill { height:100%; background:var(--amber); transition:width 0.2s; }
    .eco-audit-row { display:flex; justify-content:space-between; padding:6px 0; border-bottom:1px solid var(--border); }
    .eco-audit-row:last-child { border-bottom:none; font-weight:600; }
    .eco-audit-whatif-slider { width:100%; margin:12px 0; }
    .eco-audit-whatif-preview { font-family:var(--mono); font-size:14px; padding:8px; background:var(--bg-panel); border:1px solid var(--border); border-radius:3px; }
    .eco-audit-cta { display:block; margin:6px auto 0; padding:3px 10px; background:rgba(230,57,70,0.15); color:var(--red); border:1px solid var(--red); border-radius:3px; font-family:var(--mono); font-size:10px; letter-spacing:0.1em; text-transform:uppercase; cursor:pointer; transition:all 0.12s; }
    .eco-audit-cta:hover { background:rgba(230,57,70,0.28); }
    .eco-bal-detail-row[data-target], .eco-bal-detail-row[data-action] { cursor:pointer; transition:background 0.1s; }
    .eco-bal-detail-row[data-target]:hover, .eco-bal-detail-row[data-action]:hover { background:rgba(var(--ac),0.06); }
    .eco-forecast-assumptions { cursor:help; color:var(--text-lo); margin-left:8px; }
    .eco-peer-toggle { display:flex; gap:6px; align-items:center; margin-left:auto; font-size:10px; color:var(--text-mid); }
    .eco-peer-toggle input[type="checkbox"] { margin:0; }
    .eco-peer-chip { display:inline-block; margin-left:6px; padding:1px 6px; background:rgba(var(--ac),0.12); color:var(--cyan); border:1px solid rgba(var(--ac),0.4); border-radius:2px; font-family:var(--mono); font-size:9px; letter-spacing:0.05em; }
    .eco-diff { display:inline-block; padding:0 4px; border-radius:2px; letter-spacing:0.05em; }
    .eco-diff-pos { color:var(--green); background:rgba(6,214,160,0.1); }
    .eco-diff-neg { color:var(--red); background:rgba(230,57,70,0.1); }
    .eco-diff-flat { color:var(--text-lo); }
    .eco-cash-row { display:grid; grid-template-columns:repeat(3,1fr); gap:12px; margin-bottom:16px; }
    .eco-cash-card { background:var(--bg-tile); border:1px solid var(--line); padding:10px 12px; text-align:center; clip-path:polygon(0 0,calc(100% - 8px) 0,100% 8px,100% 100%,8px 100%,0 calc(100% - 8px)); }
    .eco-cash-label { font-family:var(--sans); font-size:10px; text-transform:uppercase; letter-spacing:1.5px; color:var(--text-lo); margin-bottom:4px; }
    .eco-cash-value { font-family:var(--mono); font-size:20px; line-height:1.2; }
    .eco-cash-hint { font-family:var(--sans); font-size:10px; color:var(--text-lo); margin-top:2px; font-style:italic; }
    .eco-runway-alert { border-color:var(--red); box-shadow:0 0 0 1px var(--red) inset; }
    .eco-extorted-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(280px, 1fr)); gap:10px; }
    .eco-extorted-card { background:rgba(252,163,17,0.06); border:1px solid rgba(252,163,17,0.28); border-left:3px solid var(--amber); border-radius:3px; padding:8px 10px; cursor:pointer; transition:background 0.12s; }
    .eco-extorted-card:hover { background:rgba(252,163,17,0.12); }
    .eco-extorted-head { display:flex; justify-content:space-between; align-items:baseline; gap:8px; margin-bottom:4px; }
    .eco-extorted-name { color:var(--text-hi); font-weight:600; font-size:13px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .eco-extorted-tribute { font-size:13px; white-space:nowrap; }
    .eco-extorted-meta { display:flex; gap:12px; font-family:var(--mono); font-size:11px; color:var(--text-mid); }
    .eco-bal-dirty-chip { display:inline-block; margin-left:6px; padding:0 4px; font-size:9px; background:rgba(252,163,17,0.18); color:var(--amber); border:1px solid rgba(252,163,17,0.4); border-radius:2px; font-family:var(--mono); letter-spacing:0.1em; cursor:help; }
    .eco-bal-spark { display:inline-flex; align-items:center; justify-content:center; width:44px; height:14px; margin:0 8px; opacity:0.85; }
    .eco-bal-spark:empty { opacity:0; }
    .eco-mini-spark { display:block; }
    .eco-bal-count { color:var(--text-lo); font-size:10px; margin-left:4px; font-family:var(--mono); }
    .eco-bal-empty { padding:12px; text-align:center; color:var(--text-lo); font-style:italic; font-size:11px; }
    .eco-bal-bucket { margin-bottom:6px; }
    .eco-bal-bucket-head { display:flex; align-items:center; gap:8px; padding:4px 6px; background:rgba(255,255,255,0.025); cursor:pointer; user-select:none; font-size:10px; text-transform:uppercase; letter-spacing:1.5px; color:var(--text-mid); }
    .eco-bal-bucket-head:hover { background:rgba(255,255,255,0.05); }
    .eco-bal-bucket-name { flex:1; }
    .eco-bal-bucket-total { font-family:var(--mono); color:var(--text-hi); }
    .eco-bal-bucket-chevron { font-size:10px; transition:transform 0.15s; }
    .eco-bal-bucket.collapsed .eco-bal-bucket-chevron { transform:rotate(-90deg); }
    .eco-bal-bucket.collapsed .eco-bal-bucket-body { display:none; }
    .eco-kpi-mini-bar { display:flex; gap:2px; align-items:flex-end; height:24px; margin-top:6px; justify-content:center; }
    .eco-kpi-mini-bar-seg { flex:0 0 auto; width:16px; border-radius:1px 1px 0 0; min-height:2px; }

    /* ── Building Cards ─────────────────────────────────────── */
    .eco-bldg-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(320px, 1fr)); gap:12px; }
    .eco-bldg-card { background:var(--bg-tile); border:1px solid var(--line); border-radius:3px; padding:14px 16px; transition:border-color 0.15s; }
    .eco-bldg-card:hover { border-color:var(--cyan-dim); }
    .eco-bldg-header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:4px; }
    .eco-bldg-name { font-family:var(--sans); font-size:14px; font-weight:600; color:var(--text-hi); }
    .eco-bldg-badges { display:flex; gap:4px; }
    .eco-bldg-badge { font-family:var(--mono); font-size:9px; padding:1px 6px; border-radius:3px; letter-spacing:1px; }
    .eco-bldg-front { background:rgba(var(--ac),0.1); border:1px solid rgba(var(--ac),0.25); color:var(--cyan-dim); }
    .eco-bldg-zone { background:rgba(155,89,182,0.1); border:1px solid rgba(155,89,182,0.2); color:var(--violet); }
    .eco-bldg-type { font-family:var(--mono); font-size:11px; color:var(--text-lo); margin-bottom:10px; letter-spacing:0.5px; }
    .eco-bldg-stats { display:grid; grid-template-columns:repeat(auto-fit, minmax(80px, 1fr)); gap:6px 12px; margin-bottom:10px; padding-bottom:10px; border-bottom:1px solid var(--border); }
    .eco-bldg-stat { display:flex; flex-direction:column; gap:2px; }
    .eco-bldg-stat-label { font-family:var(--mono); font-size:9px; text-transform:uppercase; letter-spacing:1px; color:var(--text-lo); }
    .eco-bldg-footer { display:flex; justify-content:space-between; align-items:center; font-size:12px; }
    .eco-bldg-mgr { font-family:var(--sans); font-size:12px; }
    .eco-bldg-summary { display:flex; align-items:center; gap:16px; padding:10px 14px; margin-bottom:12px; background:rgba(13,13,26,0.4); border:1px solid var(--border); border-radius:4px; font-size:12px; color:var(--text-lo); font-family:var(--mono); }
    .eco-bldg-summary-val { color:var(--text-hi); }
    .eco-bldg-controls { display:flex; gap:8px; margin-bottom:12px; align-items:center; flex-wrap:wrap; }
    .eco-bldg-sort, .eco-bldg-filter { font-family:var(--mono); font-size:11px; padding:4px 10px; background:var(--bg-panel); border:1px solid var(--border); color:var(--text-lo); border-radius:3px; cursor:pointer; text-transform:uppercase; letter-spacing:0.5px; }
    .eco-bldg-sort.active, .eco-bldg-filter.active { border-color:var(--cyan); color:var(--cyan); background:rgba(var(--ac),0.08); }
    .eco-bldg-card.heat-warn { animation:eco-heat-pulse 1.5s ease-in-out infinite; }
    @keyframes eco-heat-pulse { 0%,100%{border-color:var(--border)} 50%{border-color:rgba(231,76,60,0.6)} }
    .eco-bldg-card.not-owned { opacity:0.7; border-style:dashed; }
    .eco-bldg-card.not-owned:hover { opacity:0.9; }
    .eco-bldg-synergy { display:flex; flex-wrap:wrap; gap:4px; margin-top:6px; }
    .eco-bldg-synergy-tag { font-family:var(--mono); font-size:9px; padding:1px 6px; border-radius:3px; background:rgba(52,152,219,0.1); border:1px solid rgba(52,152,219,0.25); color:var(--blue); letter-spacing:0.5px; }
    .eco-bldg-storage { margin-top:6px; }
    .eco-bldg-storage-label { font-family:var(--mono); font-size:9px; text-transform:uppercase; letter-spacing:1px; color:var(--text-lo); margin-bottom:2px; }
    .eco-bldg-storage-bar { height:4px; background:var(--border); border-radius:2px; overflow:hidden; }
    .eco-bldg-portrait { margin-right:6px; vertical-align:middle; display:inline-block; }
    .eco-bldg-mgr { display:flex; align-items:center; gap:4px; }
    .eco-bldg-details { overflow:hidden; transition:max-height 0.25s ease; }
    .eco-bldg-details.collapsed { max-height:0; }
    .eco-bldg-details.expanded { max-height:200px; }
    .eco-bldg-expand { font-family:var(--mono); font-size:10px; color:var(--cyan-dim); cursor:pointer; background:none; border:none; padding:0; margin-top:4px; }
    .eco-bldg-expand:hover { color:var(--cyan); }
    .eco-bldg-sector-header { display:flex; align-items:center; justify-content:space-between; padding:8px 12px; margin:16px 0 8px; background:rgba(13,13,26,0.3); border:1px solid var(--border); border-radius:4px; cursor:pointer; }
    .eco-bldg-sector-header:hover { border-color:var(--cyan-dim); }
    .eco-bldg-sector-title { font-family:var(--mono); font-size:12px; color:var(--text-mid); text-transform:uppercase; letter-spacing:1px; }
    .eco-bldg-sector-toggle { font-size:10px; color:var(--text-lo); transition:transform 0.2s; }
    .eco-bldg-sector-toggle.open { transform:rotate(90deg); }
    .eco-bldg-type-icon { display:inline-block; width:16px; height:16px; margin-right:4px; vertical-align:middle; }

    /* ── Risk Meter ────────────────────────────────────────── */
    .eco-risk-meter { height:8px; border-radius:4px; position:relative;
      background:linear-gradient(90deg, var(--green) 0%, var(--amber) 50%, var(--red) 100%);
      margin-top:8px; }
    .eco-risk-marker { position:absolute; top:-3px; width:3px; height:14px;
      background:var(--text-hi); border-radius:2px; transform:translateX(-50%); }

    /* ── Pipeline Canvas ────────────────────────────────────── */
    .eco-pipeline-canvas { width:100%; height:180px; display:block; }

    /* ── Balance Bars (HTML) ──────────────────────────────────── */
    .eco-balance-bars { width:100%; }
    .eco-bal-header { font:600 10px var(--mono); color:var(--text-mid); opacity:0.6; padding:2px 0 4px; text-transform:uppercase; letter-spacing:0.5px; }
    .eco-bal-sep { height:1px; background:var(--border); margin:6px 0; }
    .eco-bal-row { display:grid; grid-template-columns:20px 1fr 2fr auto; align-items:center; gap:4px; height:28px; padding:0 4px; }
    .eco-bal-expandable { cursor:pointer; border-radius:3px; }
    .eco-bal-expandable:hover { background:rgba(255,255,255,0.03); }
    .eco-bal-icon { font-size:13px; text-align:center; opacity:0.5; }
    .eco-bal-label { font:11px "Space Grotesk", sans-serif; color:var(--text-mid); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .eco-bal-bar-bg { height:14px; background:rgba(255,255,255,0.04); border-radius:2px; overflow:hidden; }
    .eco-bal-bar { height:100%; border-radius:2px; transition:width 0.6s cubic-bezier(0.22,1,0.36,1); }
    .eco-bal-amount { font:600 11px var(--mono); text-align:right; white-space:nowrap; min-width:70px; }
    .eco-bal-warn { font:600 9px var(--mono); white-space:nowrap; padding:1px 4px; border-radius:2px; }
    .eco-bal-warn-crit { color:#e74c3c; background:rgba(231,76,60,0.12); animation:eco-blink-crit 0.6s infinite alternate; }
    .eco-bal-warn-high { color:#fca311; background:rgba(252,163,17,0.12); animation:eco-blink-warn 0.8s infinite alternate; }
    @keyframes eco-blink-crit { from{opacity:0.3} to{opacity:1} }
    @keyframes eco-blink-warn { from{opacity:0.5} to{opacity:1} }
    .eco-bal-chevron { font-size:10px; color:var(--text-mid); opacity:0.5; transition:transform 0.2s; }
    .eco-bal-chevron.open { transform:rotate(90deg); }
    .eco-bal-details { padding:0 0 4px 24px; }
    .eco-bal-detail-row { display:grid; grid-template-columns:1fr 1.5fr auto; align-items:center; gap:4px; height:22px; padding:0 4px; }
    .eco-bal-detail-label { font:10px "Space Grotesk", sans-serif; color:var(--text-mid); opacity:0.7; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .eco-bal-detail-bar-bg { height:8px; background:rgba(255,255,255,0.03); border-radius:2px; overflow:hidden; }
    .eco-bal-detail-bar { height:100%; border-radius:2px; opacity:0.6; transition:width 0.4s ease-out; }
    .eco-bal-detail-amount { font:600 10px var(--mono); text-align:right; white-space:nowrap; min-width:60px; }

    /* ── Row 3: Balance + Donut ──────────────────────────────── */
    .eco-row3 { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:16px; }
    .eco-bar-netto { font-weight:600; font-size:14px; padding-top:6px; border-top:1px solid var(--border); margin-top:6px; display:flex; justify-content:space-between; }

    /* ── Donut ─────────────────────────────────────────────── */
    .eco-donut-wrap { display:flex; flex-direction:column; align-items:center; padding-top:8px; }
    .eco-donut-legend { display:flex; flex-wrap:wrap; gap:6px 16px; margin-top:16px; font-size:12px; }
    .eco-legend-item { display:flex; align-items:center; gap:6px; }
    .eco-legend-dot { width:10px; height:10px; border-radius:50%; flex-shrink:0; }
    .eco-donut-toggles { display:flex; gap:6px; margin-top:10px; }
    .eco-donut-toggle { padding:4px 12px; font-size:11px; font-family:var(--mono);
      border:1px solid var(--border); background:var(--bg-panel); color:var(--text-lo);
      cursor:pointer; border-radius:3px; text-transform:uppercase; letter-spacing:1px; transition:all 0.12s; }
    .eco-donut-toggle.active { border-color:var(--cyan); color:var(--cyan); background:rgba(var(--ac),0.08); }

    /* ── Forecast ──────────────────────────────────────────── */
    .eco-forecast { font-size:13px; color:var(--text-lo); font-family:var(--mono); display:flex; align-items:center; gap:12px; }
    .eco-forecast-spark { flex-shrink:0; }
    @media (max-width:1024px) {
      .eco-bldg-grid { grid-template-columns:repeat(auto-fill, minmax(260px, 1fr)); }
    }
  `;
  root.appendChild(style);

  // ── Data computations ───────────────────────────────────────────────
  const avgRisk     = subs.length ? subs.reduce((s, sub) => s + sub.risk, 0) / subs.length : 0;

  const incomeLines  = p.dailyBalance?.income ?? [];
  const expenseLines = p.dailyBalance?.expenses ?? [];
  const dailyIncome  = incomeLines.reduce((s, l) => s + l.amount, 0);
  const dailyExpense = expenseLines.reduce((s, l) => s + l.amount, 0);
  const dailyNet     = dailyIncome - dailyExpense;

  // DTO v2 split: Clean vs Dirty / Passive vs Active.
  // Legal-flag classifies clean-flow; subsystem groups passive (buildings,
  // subs, drones) vs active (action-driven cashflow from ActionLog).
  const legalIncome   = incomeLines.filter(l => l.legal).reduce((s, l) => s + l.amount, 0);
  const dirtyIncome   = incomeLines.filter(l => !l.legal).reduce((s, l) => s + l.amount, 0);
  const passiveExpense = expenseLines.filter(l => l.subsystem === 'passive').reduce((s, l) => s + l.amount, 0);
  const actionExpense  = expenseLines.filter(l => l.subsystem === 'active').reduce((s, l) => s + l.amount, 0);

  const auditPct     = Math.round(avgRisk * 100);
  const auditColor   = avgRisk > AUDIT_THRESH_CRIT ? 'var(--red)' : avgRisk > AUDIT_THRESH_WARN ? 'var(--amber)' : 'var(--green)';
  const auditLabel   = avgRisk > AUDIT_THRESH_CRIT ? t('economy.audit_high') : avgRisk > AUDIT_THRESH_WARN ? t('economy.audit_mid') : t('economy.audit_low');

  // Forecast uses the complete Netto (legal + dirty, fixed + action). Prior
  // versions only summed legal_income which understated cashflow for crime-
  // heavy strategies.
  const forecast7d   = Math.round(p.cleanCash + dailyNet * 7);

  // Runway: how many days of clean-cash runway remain at current burn rate.
  // Only meaningful when net is negative — otherwise render "cashflow positive".
  const runwayDays   = dailyNet < 0 ? p.cleanCash / Math.abs(dailyNet) : Infinity;

  // Vorzyklus-Diff data — rendered as `↑ +X` / `↓ -X` badges on each
  // KPI card. Uses the cached EconomySnapshot history; falls back to
  // empty badges when history hasn't been fetched yet (refresh call
  // below will re-render them on resolve).
  const history     = state.sessionId ? getCachedHistory(state.sessionId) : [];
  const prevSnap    = previousSnapshot(history);
  function fmtCashDelta(v: number): string { return formatCash(Math.round(v)); }
  const diffIncome  = prevSnap
    ? renderDeltaHTML(describeDelta(prevSnap.legalIncome + prevSnap.dirtyIncome, dailyIncome), fmtCashDelta)
    : '';
  const diffExpense = prevSnap
    ? renderDeltaHTML(describeDelta(prevSnap.fixedExpenses + prevSnap.actionExpenses, dailyExpense, true), fmtCashDelta)
    : '';
  const diffClean   = prevSnap
    ? renderDeltaHTML(describeDelta(prevSnap.cleanCash, p.cleanCash), fmtCashDelta)
    : '';
  const diffDirty   = prevSnap
    ? renderDeltaHTML(describeDelta(prevSnap.dirtyCash, p.dirtyCash, true), fmtCashDelta)
    : '';

  // Audit-tooltip explains the risk formula + thresholds so the player can
  // predict when an audit becomes likely.
  const auditTooltipText = [
    t('economy.audit_tooltip_formula') ?? 'Audit-Risk = avg(subsidiary risk) × f(Federal Heat)',
    `Subs: ${subs.length}, ${t('economy.audit_avg_risk') ?? 'Durchschnittsrisiko'}: ${auditPct}%`,
    `${t('economy.audit_fed_heat') ?? 'Federal Heat'}: ${Math.round(p.federalHeat)}/100`,
    `${t('economy.audit_thresholds') ?? 'Schwellen'}: ${Math.round(AUDIT_THRESH_WARN * 100)}% / ${Math.round(AUDIT_THRESH_CRIT * 100)}%`,
  ].join('\n');

  // Net Worth trend (vs previous day)
  const nwHist = p.netWorthHistory ?? [];
  const nwTrend = nwHist.length >= 2 ? nwHist[nwHist.length - 1] - nwHist[nwHist.length - 2] : 0;
  const nwTrendPct = nwHist.length >= 2 && nwHist[nwHist.length - 2] !== 0
    ? ((nwTrend / nwHist[nwHist.length - 2]) * 100) : 0;
  const nwTrendHtml = nwHist.length >= 2
    ? `<div class="eco-kpi-trend ${nwTrend >= 0 ? 'eco-kpi-trend-up' : 'eco-kpi-trend-down'}">${nwTrend >= 0 ? '&#9650;' : '&#9660;'} ${nwTrend >= 0 ? '+' : '\u2212'}${formatCash(Math.round(Math.abs(nwTrend)))} (${Math.abs(nwTrendPct).toFixed(1)}%)</div>`
    : '';

  // Donut segments (Assets mode — from server-computed breakdown)
  const ASSET_COLORS: Record<string, string> = {
    clean_cash: '#2ecc71', dirty_cash: '#e74c3c', buildings: '#3498db',
    subsidiaries: '#f39c12', drones: '#00cec9', runners: '#9b59b6',
  };
  const assetSegments = (p.assetBreakdown ?? []).map(seg => ({
    value: seg.value,
    color: ASSET_COLORS[seg.key] ?? '#636e72',
    label: t(`economy.asset_${seg.key}`),
  })).filter(s => s.value > 0);

  // Donut segments (Revenue mode) — DTO v2: category key used for color + label via i18n.
  const revenueSegments = incomeLines.map(line => ({
    value: line.amount,
    color: CATEGORY_COLORS[line.key] ?? (line.legal ? '#06d6a0' : '#f4a261'),
    label: balanceCategoryLabel(line),
  })).filter(s => s.value > 0);

  // ── KPI mini-bar breakdown ──────────────────────────────────────────
  const incomeMax = Math.max(...incomeLines.map(l => l.amount), 1);
  const incomeMiniBars = incomeLines.map(line => {
    const h = Math.max(2, Math.round((line.amount / incomeMax) * 24));
    const barColor = line.legal ? 'var(--green)' : 'var(--amber)';
    return `<div class="eco-kpi-mini-bar-seg" style="height:${h}px;background:${barColor}" title="${balanceCategoryLabel(line)}: ${formatCash(Math.round(line.amount))}"></div>`;
  }).join('');

  const expenseMax = Math.max(...expenseLines.map(l => l.amount), 1);
  const expenseMiniBars = expenseLines.map(line => {
    const h = Math.max(2, Math.round((line.amount / expenseMax) * 24));
    return `<div class="eco-kpi-mini-bar-seg" style="height:${h}px;background:var(--red)" title="${balanceCategoryLabel(line)}: ${formatCash(Math.round(line.amount))}"></div>`;
  }).join('');

  const netColor = dailyNet >= 0 ? 'var(--cyan)' : 'var(--red)';
  const netStr   = dailyNet >= 0
    ? '+' + formatCash(Math.round(dailyNet))
    : '\u2212' + formatCash(Math.round(Math.abs(dailyNet)));

  // Donut legend
  function buildLegend(segments: DonutSegment[]): string {
    return segments.map(s =>
      `<div class="eco-legend-item">
        <div class="eco-legend-dot" style="background:${s.color}"></div>
        <span style="color:var(--text-mid)">${s.label}: <span style="font-family:var(--mono);color:var(--text-hi)">${formatCash(Math.round(s.value))}</span></span>
      </div>`
    ).join('');
  }

  // ── Build HTML ──────────────────────────────────────────────────────
  const wrapper = document.createElement('div');
  wrapper.innerHTML = `
    <div class="screen-title">${t('economy.title')}</div>

    <div class="eco-tabs tabs">
      <button class="eco-tab tab active" id="etab-overview">${t('economy.tab_overview')}</button>
      <button class="eco-tab tab" id="etab-clean">${t('economy.tab_clean') ?? 'Clean Flow'}</button>
      <button class="eco-tab tab" id="etab-dirty">${t('economy.tab_dirty') ?? 'Dirty Flow'}</button>
      <button class="eco-tab tab" id="etab-audit">${t('economy.tab_audit') ?? 'Audit-Risk'}</button>
      <button class="eco-tab tab" id="etab-buildings">${t('economy.tab_buildings')}</button>
      <button class="eco-tab tab" id="etab-subs">${t('economy.tab_subs')}</button>
      <button class="eco-tab tab" id="etab-vehicles">${t('economy.tab_vehicles')}</button>
      <button class="eco-tab tab" id="etab-storage">${t('economy.tab_storage')}</button>
    </div>

    <!-- ═══ Übersicht ═══ -->
    <div id="eview-overview">

      <!-- Row 1: KPI Cards -->
      <div class="eco-kpi-row">
        <div class="eco-kpi-card">
          <div class="eco-kpi-icon">${iconNetWorth()}</div>
          <div class="eco-kpi-value" id="eco-nw-ticker" style="color:var(--amber)">${formatCash(Math.round(p.netWorth))}</div>
          <div class="eco-kpi-label">${t('economy.kpi_net_worth')}</div>
          ${nwTrendHtml}
          <div class="eco-kpi-spark"><canvas id="eco-nw-sparkline" width="120" height="32" style="width:120px;height:32px"></canvas></div>
        </div>
        <div class="eco-kpi-card">
          <div class="eco-kpi-icon">${iconIncome()}</div>
          <div class="eco-kpi-value" style="color:var(--green)"><span style="font-size:14px">&#9650;</span> ${formatCash(Math.round(dailyIncome))}</div>
          <div class="eco-kpi-label">${t('economy.kpi_daily_income')}</div>
          <div class="eco-kpi-diff" data-diff="income">${diffIncome}</div>
          <div class="eco-kpi-split">
            <span style="color:var(--green)">${t('economy.split_legal') ?? 'legal'} ${formatCash(Math.round(legalIncome))}</span>
            <span style="color:var(--amber)">${t('economy.split_dirty') ?? 'dirty'} ${formatCash(Math.round(dirtyIncome))}</span>
          </div>
          <div class="eco-kpi-mini-bar">${incomeMiniBars}</div>
        </div>
        <div class="eco-kpi-card">
          <div class="eco-kpi-icon">${iconExpenses()}</div>
          <div class="eco-kpi-value" style="color:var(--red)"><span style="font-size:14px">&#9660;</span> ${formatCash(Math.round(dailyExpense))}</div>
          <div class="eco-kpi-label">${t('economy.kpi_daily_expenses')}</div>
          <div class="eco-kpi-diff" data-diff="expense">${diffExpense}</div>
          <div class="eco-kpi-split">
            <span style="color:var(--text-lo)">${t('economy.split_fixed') ?? 'fix'} ${formatCash(Math.round(passiveExpense))}</span>
            <span style="color:var(--amber)">${t('economy.split_actions') ?? 'akt'} ${formatCash(Math.round(actionExpense))}</span>
          </div>
          <div class="eco-kpi-mini-bar">${expenseMiniBars}</div>
        </div>
        <div class="eco-kpi-card" title="${auditTooltipText}">
          <div class="eco-kpi-icon">${iconAuditRisk()}</div>
          <div class="eco-kpi-value" style="color:${auditColor}">${auditPct}% <span style="font-size:11px;color:var(--text-lo);cursor:help">\u24D8</span></div>
          <div class="eco-kpi-label">${t('economy.kpi_audit_risk')}</div>
          <div class="eco-risk-meter">
            <div class="eco-risk-marker" style="left:${auditPct}%"></div>
          </div>
          <div style="font-size:10px;color:${auditColor};margin-top:4px;text-transform:uppercase;letter-spacing:1px">${auditLabel}</div>
        </div>
      </div>

      <!-- Row 1.5: Clean/Dirty cash split + Runway widget -->
      <div class="eco-cash-row">
        <div class="eco-cash-card">
          <div class="eco-cash-label">${t('economy.clean_cash') ?? 'Clean Cash'}</div>
          <div class="eco-cash-value" style="color:var(--green)">${formatCash(Math.round(p.cleanCash))}</div>
          <div class="eco-kpi-diff" data-diff="clean">${diffClean}</div>
        </div>
        <div class="eco-cash-card">
          <div class="eco-cash-label">${t('economy.dirty_cash') ?? 'Dirty Cash'}</div>
          <div class="eco-cash-value" style="color:${p.dirtyCash > 0 ? 'var(--amber)' : 'var(--text-lo)'}">${formatCash(Math.round(p.dirtyCash))}</div>
          ${p.dirtyCash === 0 ? `<div class="eco-cash-hint">${t('economy.dirty_cash_hint') ?? 'Keine Kriminalerträge aktiv'}</div>` : ''}
          <div class="eco-kpi-diff" data-diff="dirty">${diffDirty}</div>
        </div>
        <div class="eco-cash-card eco-runway ${runwayDays < 7 && runwayDays !== Infinity ? 'eco-runway-alert' : ''}">
          <div class="eco-cash-label">${t('economy.runway') ?? 'Liquidität-Runway'}</div>
          ${runwayDays === Infinity
            ? `<div class="eco-cash-value" style="color:var(--green);font-size:16px">${t('economy.runway_positive') ?? 'Cashflow positiv'}</div>`
            : `<div class="eco-cash-value" style="color:${runwayDays < 7 ? 'var(--red)' : runwayDays < 14 ? 'var(--amber)' : 'var(--text-hi)'}">${runwayDays.toFixed(1)} ${t('economy.runway_days_unit') ?? 'Tage'}</div>
               <div class="eco-cash-hint">${t('economy.runway_hint') ?? 'bei aktuellem Netto-Tempo'}</div>`
          }
        </div>
      </div>

      <!-- Row 2: Cash-Flow Pipeline (Canvas) -->
      <div class="panel" style="margin-bottom:16px">
        <div class="panel-title">${t('economy.panel_cashflow')}</div>
        <canvas class="eco-pipeline-canvas" id="eco-pipeline-canvas"></canvas>
        <div class="eco-pipeline-data" style="display:none"
          data-clean-cash="${Math.round(p.cleanCash)}"
          data-dirty-cash="${Math.round(p.dirtyCash)}"
          data-subs="${subs.length}"
          data-audit="${auditPct}"
          data-daily-income="${Math.round(dailyIncome)}">
        </div>
      </div>

      <!-- Row 3: Balance Bars (Canvas) + Donut -->
      <div class="eco-row3">
        <div class="panel">
          <div class="panel-title">${t('economy.panel_daily')}</div>
          <div class="eco-balance-bars" id="eco-balance-bars">${buildBalanceBarsHTML(incomeLines, expenseLines, dailyIncome)}</div>
          <div class="eco-bar-netto">
            <span style="color:var(--text-mid)">${t('economy.netto')}</span>
            <span style="font-family:var(--mono);color:${netColor}">${netStr}</span>
          </div>
        </div>
        <div class="panel">
          <div class="panel-title">${t('economy.donut_title')}</div>
          <div class="eco-donut-toggles">
            <button class="eco-donut-toggle active" data-donut-mode="assets">${t('economy.donut_assets')}</button>
            <button class="eco-donut-toggle" data-donut-mode="revenue">${t('economy.donut_revenue')}</button>
          </div>
          <div class="eco-donut-wrap">
            <canvas id="eco-donut-canvas" width="200" height="200" style="width:200px;height:200px"></canvas>
            <div class="eco-donut-legend">${buildLegend(assetSegments)}</div>
          </div>
        </div>
      </div>

      <!-- Row 3.5: Laufende Schutzgelder (v1.11.0+) — only render when there are active extortions -->
      ${(p.extortedBuildings && p.extortedBuildings.length > 0) ? `
      <div class="panel" style="margin-bottom:16px">
        <div class="panel-title">${t('economy.panel_extorted') ?? 'Laufende Schutzgelder'} <span style="font-size:11px;color:var(--text-lo);margin-left:8px">(${p.extortedBuildings.length})</span></div>
        <div class="eco-extorted-grid">
          ${p.extortedBuildings.map(ex => {
            const daysLeft = ex.expiresAt - (p.cycle ?? 0);
            const collectGap = (p.cycle ?? 0) - ex.lastCollected;
            const decayDaysLeft = 3 - collectGap;
            const daysLeftColor = daysLeft <= 2 ? 'var(--red)' : daysLeft <= 5 ? 'var(--amber)' : 'var(--green)';
            const decayColor = decayDaysLeft <= 1 ? 'var(--red)' : decayDaysLeft <= 2 ? 'var(--amber)' : 'var(--text-mid)';
            const name = ex.name || ex.nodeId;
            return `<div class="eco-extorted-card" data-node-id="${ex.nodeId}" title="${t('economy.extorted_click_hint') ?? 'Klick → auf der Karte anzeigen'}">
              <div class="eco-extorted-head">
                <span class="eco-extorted-name">${name}</span>
                <span class="eco-extorted-tribute" style="color:var(--amber);font-family:var(--mono)">+${formatCash(Math.round(ex.dailyTribute))}/Tag</span>
              </div>
              <div class="eco-extorted-meta">
                <span style="color:${daysLeftColor}">${daysLeft}d ${t('economy.extorted_remaining') ?? 'verbleibend'}</span>
                <span style="color:${decayColor}">${t('economy.extorted_collect_gap') ?? 'Letzter Besuch'}: ${collectGap}d</span>
                ${ex.sectorId ? `<span style="color:var(--text-lo)">${ex.sectorId}</span>` : ''}
              </div>
            </div>`;
          }).join('')}
        </div>
      </div>
      ` : ''}

      <!-- Row 4: Forecast (bottom-up when history available) -->
      <div class="panel eco-forecast" id="eco-forecast-panel">
        <span class="eco-forecast-text" id="eco-forecast-text">
          <span style="color:var(--cyan)">&#9656;</span> ${t('economy.forecast_text', { amount: formatCash(forecast7d) })}
        </span>
        <span id="eco-forecast-assumptions" class="eco-forecast-assumptions" style="display:none" title="">\u24D8</span>
        ${(p.netWorthHistory && p.netWorthHistory.length > 1) ? '<canvas id="eco-forecast-sparkline" class="eco-forecast-spark" width="100" height="24" style="width:100px;height:24px"></canvas>' : ''}
      </div>

    </div>

    <!-- ═══ Clean Flow ═══ -->
    <div id="eview-clean" style="display:none">
      <div class="panel">
        <div class="panel-title">${t('economy.panel_clean_flow') ?? 'Saubere Cashflow-Quellen'}</div>
        <div class="eco-flow-grid" id="eco-clean-flow-grid"></div>
      </div>
      <div class="panel" style="margin-top:16px">
        <div class="panel-title">${t('economy.panel_clean_history') ?? 'Historie'}</div>
        <canvas id="eco-clean-history" width="800" height="140" style="width:100%;height:140px"></canvas>
      </div>
    </div>

    <!-- ═══ Dirty Flow ═══ -->
    <div id="eview-dirty" style="display:none">
      <div class="panel">
        <div class="panel-title">${t('economy.panel_dirty_flow') ?? 'Dirty Cashflow-Quellen'}</div>
        <div class="eco-flow-grid" id="eco-dirty-flow-grid"></div>
      </div>
      <div class="panel" style="margin-top:16px">
        <div class="panel-title">${t('economy.panel_launder_queue') ?? 'Launder-Queue'}</div>
        <div id="eco-launder-widget"></div>
      </div>
      <div class="panel" style="margin-top:16px">
        <div class="panel-title">${t('economy.panel_dirty_history') ?? 'Historie'}</div>
        <canvas id="eco-dirty-history" width="800" height="140" style="width:100%;height:140px"></canvas>
      </div>
    </div>

    <!-- ═══ Audit-Risk ═══ -->
    <div id="eview-audit" style="display:none">
      <div class="panel">
        <div class="panel-title">${t('economy.panel_audit_formula') ?? 'Risk-Formel'}</div>
        <div id="eco-audit-breakdown" style="font-family:var(--mono); font-size:12px; line-height:1.9"></div>
      </div>
      <div class="panel" style="margin-top:16px">
        <div class="panel-title">${t('economy.panel_audit_whatif') ?? 'Was-wenn Simulator'}</div>
        <div id="eco-audit-whatif"></div>
      </div>
    </div>

    <!-- ═══ Gebäude ═══ -->
    <div id="eview-buildings" style="display:none">
      <div class="panel">
        <div class="panel-title">${t('economy.panel_buildings')} <span style="font-size:11px;color:var(--text-lo);margin-left:8px">(${buildings.length})</span></div>

        <!-- Summary KPI bar -->
        ${buildings.length > 0 ? (() => {
          const totalProfit = buildings.reduce((s, b) => s + ((b.hasManager ? b.income : 0) - (b.rent ?? 0)), 0);
          const avgHeat = Math.round(buildings.reduce((s, b) => s + b.heat, 0) / buildings.length);
          return `<div class="eco-bldg-summary">
            <span>${t('economy.summary_buildings', { count: String(buildings.length) })}</span>
            <span>\u00B7</span>
            <span>${t('economy.summary_profit', { amount: (totalProfit >= 0 ? '+' : '\u2212') + formatCash(Math.round(Math.abs(totalProfit))) })}</span>
            <span>\u00B7</span>
            <span>${t('economy.summary_avg_heat', { value: String(avgHeat) })}</span>
          </div>`;
        })() : ''}

        <!-- Sort/Filter controls -->
        <div class="eco-bldg-controls">
          <button class="eco-bldg-sort active" data-sort="profit">${t('economy.sort_profit')}</button>
          <button class="eco-bldg-sort" data-sort="heat">${t('economy.sort_heat')}</button>
          <button class="eco-bldg-sort" data-sort="zone">${t('economy.sort_zone')}</button>
          <span style="width:1px;height:16px;background:var(--border);margin:0 4px"></span>
          <button class="eco-bldg-filter active" data-filter="all">${t('economy.filter_all')}</button>
          <button class="eco-bldg-filter" data-filter="fronts">${t('economy.filter_fronts')}</button>
          <button class="eco-bldg-filter" data-filter="profitable">${t('economy.filter_profitable')}</button>
        </div>

        ${buildings.length === 0
          ? `<div style="color:var(--text-lo);font-size:13px;padding:16px 0;text-align:center">${t('economy.no_buildings')}</div>`
          : `<div class="eco-bldg-grid" id="eco-bldg-owned-grid">
              ${buildings.map(b => buildBuildingCard(b, p.runners, true)).join('')}
            </div>`}
        <button class="dash-link" style="margin-top:12px" id="eco-btn-buy-building">${t('economy.btn_buy_building')}</button>
      </div>

      <!-- Non-owned discovered buildings (per sector) -->
      ${(() => {
        const nonOwned = fixture.player.buildings.filter(b => !b.owned);
        if (nonOwned.length === 0) return '';
        const bySector = new Map<string, typeof nonOwned>();
        for (const b of nonOwned) {
          const arr = bySector.get(b.sector) ?? [];
          arr.push(b);
          bySector.set(b.sector, arr);
        }
        let html = `<div class="panel" style="margin-top:12px">
          <div class="panel-title">${t('economy.discovered_buildings')} <span style="font-size:11px;color:var(--text-lo);margin-left:8px">(${nonOwned.length})</span></div>`;
        for (const [sector, sectorBuildings] of bySector) {
          const sectorId = sector.replace(/[^a-zA-Z0-9]/g, '_');
          html += `
            <div class="eco-bldg-sector-header" data-sector-toggle="${sectorId}">
              <span class="eco-bldg-sector-title">${t('economy.section_sector', { name: sector })} (${sectorBuildings.length})</span>
              <span class="eco-bldg-sector-toggle" id="eco-sector-arrow-${sectorId}">\u25B6</span>
            </div>
            <div class="eco-bldg-grid" id="eco-sector-${sectorId}" style="display:none">
              ${sectorBuildings.map(b => buildBuildingCard(b, p.runners, false)).join('')}
            </div>`;
        }
        html += '</div>';
        return html;
      })()}
    </div>

    <!-- ═══ Subsidiaries ═══ -->
    <div id="eview-subs" style="display:none">
      <div class="panel">
        <div class="panel-title">${t('economy.panel_subs')}</div>
        <table class="eco-table dtable">
          <thead><tr><th>${t('economy.col_name')}</th><th>${t('economy.col_tier')}</th><th>${t('economy.col_dirty_day')}</th><th>${t('economy.col_audit_risk')}</th></tr></thead>
          <tbody>
            ${subs.map(s => `<tr>
              <td style="color:var(--text-hi)">${s.name}${s.frozen ? ' <span style="font-family:var(--mono);font-size:11px;color:var(--red)">[FROZEN]</span>' : ''}</td>
              <td><span class="tier-badge tier-${s.tier}">${s.tier.toUpperCase()}</span></td>
              <td style="font-family:var(--mono);color:var(--amber)">${formatCash(Math.round(s.dirty))}</td>
              <td>
                <div style="display:flex; align-items:center; gap:6px">
                  <div style="flex:1; height:4px; background:var(--border); border-radius:2px; max-width:60px">
                    <div style="height:100%; width:${Math.round(s.risk * 100)}%; background:${s.risk > 0.6 ? 'var(--red)' : s.risk > 0.3 ? 'var(--amber)' : 'var(--green)'}; border-radius:2px"></div>
                  </div>
                  <span style="font-family:var(--mono);font-size:11px;color:${s.risk > 0.6 ? 'var(--red)' : s.risk > 0.3 ? 'var(--amber)' : 'var(--green)'}">${Math.round(s.risk * 100)}%</span>
                </div>
              </td>
            </tr>`).join('')}
          </tbody>
        </table>
        <button class="dash-link" style="margin-top:8px">${t('economy.btn_new_sub')}</button>
      </div>
    </div>

    <!-- ═══ Fahrzeuge ═══ -->
    <div id="eview-vehicles" style="display:none">
      <div class="panel">
        <div class="panel-title">${t('economy.panel_vehicles')}</div>
        <table class="eco-table dtable">
          <thead><tr><th>${t('economy.col_name')}</th><th>${t('economy.col_type')}</th><th>${t('economy.col_status')}</th><th>${t('economy.col_runner')}</th></tr></thead>
          <tbody>
            ${vehicles.map(v => `<tr>
              <td style="color:var(--text-hi)">${v}</td>
              <td style="font-family:var(--mono);font-size:11px;color:var(--text-lo)">\u2014</td>
              <td style="color:var(--green);font-size:12px">${t('economy.vehicle_active')}</td>
              <td style="color:var(--text-mid)">\u2014</td>
            </tr>`).join('')}
          </tbody>
        </table>
      </div>
    </div>

    <!-- ═══ Lager ═══ -->
    <div id="eview-storage" style="display:none">
      <div class="panel">
        <div class="panel-title">${t('economy.panel_storage')}</div>
        ${(p.storage ?? []).map(s => {
          const pct   = Math.round(s.usedPercent);
          const color = pct > 80 ? 'var(--amber)' : pct > 50 ? 'var(--cyan)' : 'var(--green)';
          return `<div style="margin-bottom:12px">
          <div style="display:flex; justify-content:space-between; font-size:13px; margin-bottom:4px">
            <span style="color:var(--text-mid)">${s.name}</span>
            <span style="font-family:var(--mono); color:${color}">${pct}% / ${s.capacityUnits}u</span>
          </div>
          <div style="height:6px; background:var(--border); border-radius:3px; overflow:hidden">
            <div style="height:100%; width:${pct}%; background:${color}; border-radius:3px"></div>
          </div>
          ${pct > 80 ? `<div style="font-size:11px; color:var(--amber); margin-top:3px">${t('economy.storage_warning')}</div>` : ''}
        </div>`;
        }).join('')}
      </div>
    </div>
  `;

  // ── Tab switching ───────────────────────────────────────────────────
  const tabDefs = [
    { btn: '#etab-overview',  view: '#eview-overview'  },
    { btn: '#etab-clean',     view: '#eview-clean'     },
    { btn: '#etab-dirty',     view: '#eview-dirty'     },
    { btn: '#etab-audit',     view: '#eview-audit'     },
    { btn: '#etab-buildings', view: '#eview-buildings' },
    { btn: '#etab-subs',      view: '#eview-subs'      },
    { btn: '#etab-vehicles',  view: '#eview-vehicles'  },
    { btn: '#etab-storage',   view: '#eview-storage'   },
  ];

  let animFrameId = 0;
  const pipelineParticles: Particle[][] = [];
  let pipelineHoveredNode = -1;
  let lastPipelineNodes: PipelineNode[] = [];

  function startAnimation(): void {
    const pipelineCanvas = wrapper.querySelector('#eco-pipeline-canvas') as HTMLCanvasElement | null;

    const pipelineData: PipelineData = {
      incomeLines,
      subsCount: subs.length,
      subNames: subs.map(s => s.name),
      auditPct,
      federalHeat: p.federalHeat,
      cleanCash: p.cleanCash,
      dirtyCash: p.dirtyCash,
      dailyIncome,
    };

    // Pipeline tooltip element
    let tooltip = wrapper.querySelector('.eco-pipeline-tooltip') as HTMLElement | null;
    if (!tooltip) {
      tooltip = document.createElement('div');
      tooltip.className = 'eco-pipeline-tooltip';
      tooltip.style.cssText = 'position:absolute;display:none;pointer-events:none;background:rgba(10,10,20,0.95);border:1px solid var(--border);border-radius:4px;padding:6px 10px;font-size:11px;font-family:var(--mono);color:var(--text-mid);white-space:pre;z-index:10;box-shadow:0 4px 12px rgba(0,0,0,0.5);line-height:1.5';
      const pipelineWrap = pipelineCanvas?.parentElement;
      if (pipelineWrap) {
        pipelineWrap.style.position = 'relative';
        pipelineWrap.appendChild(tooltip);
      }
    }

    // Hover handler — isPointInPath per node
    if (pipelineCanvas && !pipelineCanvas.dataset.hoverBound) {
      pipelineCanvas.dataset.hoverBound = '1';
      const ctx = pipelineCanvas.getContext('2d');
      pipelineCanvas.addEventListener('mousemove', (e: MouseEvent) => {
        if (!ctx || lastPipelineNodes.length === 0) return;
        const rect = pipelineCanvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        const mx = (e.clientX - rect.left) * dpr;
        const my = (e.clientY - rect.top) * dpr;
        let found = -1;
        for (let ni = 0; ni < lastPipelineNodes.length; ni++) {
          const n = lastPipelineNodes[ni];
          if (n.path && ctx.isPointInPath(n.path, mx / dpr, my / dpr)) {
            found = ni;
            break;
          }
        }
        pipelineHoveredNode = found;
        pipelineCanvas.style.cursor = found >= 0 ? 'pointer' : '';
        if (tooltip) {
          if (found >= 0 && lastPipelineNodes[found].tooltip) {
            tooltip.textContent = lastPipelineNodes[found].tooltip!.join('\n');
            tooltip.style.display = '';
            const node = lastPipelineNodes[found];
            tooltip.style.left = `${node.x + node.w / 2}px`;
            tooltip.style.top = `${node.y - 8}px`;
            tooltip.style.transform = 'translate(-50%, -100%)';
            tooltip.style.borderColor = node.color;
          } else {
            tooltip.style.display = 'none';
          }
        }
      });
      pipelineCanvas.addEventListener('mouseleave', () => {
        pipelineHoveredNode = -1;
        pipelineCanvas.style.cursor = '';
        if (tooltip) tooltip.style.display = 'none';
      });
    }

    function animate(ts: number): void {
      if (pipelineCanvas) lastPipelineNodes = drawPipeline(pipelineCanvas, pipelineData, ts, pipelineParticles, pipelineHoveredNode);
      animFrameId = requestAnimationFrame(animate);
    }
    animFrameId = requestAnimationFrame(animate);
  }

  function stopAnimation(): void {
    if (animFrameId) {
      cancelAnimationFrame(animFrameId);
      animFrameId = 0;
    }
  }

  tabDefs.forEach(({ btn }, idx) => {
    wrapper.querySelector(btn)!.addEventListener('click', () => {
      tabDefs.forEach(({ btn: b, view: v }, i) => {
        (wrapper.querySelector(b) as HTMLElement).classList.toggle('active', i === idx);
        (wrapper.querySelector(v) as HTMLElement).style.display = i === idx ? '' : 'none';
      });
      // Start/stop animation based on overview tab visibility
      if (idx === 0) startAnimation();
      else stopAnimation();
      // Hydrate portraits when buildings tab becomes visible
      if (idx === 1) requestAnimationFrame(() => hydratePortraits(wrapper));
    });
  });

  // ── Building sort/filter ───────────────────────────────────────────
  const sortBtns = wrapper.querySelectorAll('.eco-bldg-sort');
  const filterBtns = wrapper.querySelectorAll('.eco-bldg-filter');
  const ownedGrid = wrapper.querySelector('#eco-bldg-owned-grid') as HTMLElement | null;

  function applyBuildingSort(sortBy: string): void {
    if (!ownedGrid) return;
    const cards = [...ownedGrid.querySelectorAll('.eco-bldg-card')] as HTMLElement[];
    cards.sort((a, b) => {
      if (sortBy === 'profit') return parseFloat(b.dataset.profit ?? '0') - parseFloat(a.dataset.profit ?? '0');
      if (sortBy === 'heat') return parseFloat(b.dataset.heat ?? '0') - parseFloat(a.dataset.heat ?? '0');
      if (sortBy === 'zone') return (a.dataset.zone ?? '').localeCompare(b.dataset.zone ?? '');
      return 0;
    });
    for (const card of cards) ownedGrid.appendChild(card);
  }

  function applyBuildingFilter(filterBy: string): void {
    if (!ownedGrid) return;
    const cards = ownedGrid.querySelectorAll('.eco-bldg-card') as NodeListOf<HTMLElement>;
    cards.forEach(card => {
      let show = true;
      if (filterBy === 'fronts') show = card.dataset.front === 'true';
      if (filterBy === 'profitable') show = parseFloat(card.dataset.profit ?? '0') > 0;
      card.style.display = show ? '' : 'none';
    });
  }

  sortBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      sortBtns.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      applyBuildingSort(btn.getAttribute('data-sort') ?? 'profit');
    });
  });

  filterBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      filterBtns.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      applyBuildingFilter(btn.getAttribute('data-filter') ?? 'all');
    });
  });

  // ── Sector collapse/expand ────────────────────────────────────────
  wrapper.querySelectorAll('.eco-bldg-sector-header').forEach(header => {
    header.addEventListener('click', () => {
      const sectorId = header.getAttribute('data-sector-toggle');
      if (!sectorId) return;
      const grid = wrapper.querySelector(`#eco-sector-${sectorId}`) as HTMLElement | null;
      const arrow = wrapper.querySelector(`#eco-sector-arrow-${sectorId}`) as HTMLElement | null;
      if (grid) {
        const isOpen = grid.style.display !== 'none';
        grid.style.display = isOpen ? 'none' : '';
        if (arrow) arrow.classList.toggle('open', !isOpen);
      }
    });
  });

  // ── Detail expand/collapse ────────────────────────────────────────
  wrapper.querySelectorAll('.eco-bldg-expand').forEach(btn => {
    btn.addEventListener('click', () => {
      const targetId = btn.getAttribute('data-target');
      if (!targetId) return;
      const details = wrapper.querySelector(`#${targetId}`) as HTMLElement | null;
      if (details) {
        const isCollapsed = details.classList.contains('collapsed');
        details.classList.toggle('collapsed', !isCollapsed);
        details.classList.toggle('expanded', isCollapsed);
        btn.textContent = isCollapsed ? t('economy.collapse_details') + ' \u25BE' : t('economy.expand_details') + ' \u25B8';
      }
    });
  });

  // ── Donut toggle ────────────────────────────────────────────────────
  const donutToggles = wrapper.querySelectorAll('.eco-donut-toggle');
  donutToggles.forEach(btn => {
    btn.addEventListener('click', () => {
      donutToggles.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const mode = btn.getAttribute('data-donut-mode');
      const segments = mode === 'revenue' ? revenueSegments : assetSegments;
      const canvas = wrapper.querySelector('#eco-donut-canvas') as HTMLCanvasElement | null;
      if (canvas) drawDonutChart(canvas, segments, formatCash(Math.round(p.netWorth)));
      const legend = wrapper.querySelector('.eco-donut-legend');
      if (legend) legend.innerHTML = buildLegend(segments);
    });
  });

  // ── Donut hover-glow ──────────────────────────────────────────────────
  let currentDonutSegments = assetSegments;
  let donutHoveredIndex = -1;

  // Update segment ref when toggle changes
  donutToggles.forEach(btn => {
    btn.addEventListener('click', () => {
      const mode = btn.getAttribute('data-donut-mode');
      currentDonutSegments = mode === 'revenue' ? revenueSegments : assetSegments;
      donutHoveredIndex = -1;
    });
  });

  // Hit-test: which donut segment is the mouse over?
  function donutHitTest(mx: number, my: number, segments: DonutSegment[]): number {
    const size = 200, cx = size / 2, cy = size / 2, outerR = 89, innerR = 55;
    const dx = mx - cx, dy = my - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < innerR || dist > outerR) return -1;
    const total = segments.reduce((s, seg) => s + seg.value, 0);
    if (total === 0) return -1;
    let mouseAngle = Math.atan2(dy, dx) + Math.PI / 2; // offset by -90deg start
    if (mouseAngle < 0) mouseAngle += Math.PI * 2;
    let cumAngle = 0;
    for (let i = 0; i < segments.length; i++) {
      cumAngle += (segments[i].value / total) * Math.PI * 2;
      if (mouseAngle <= cumAngle) return i;
    }
    return -1;
  }

  requestAnimationFrame(() => {
    const donutCanvas = wrapper.querySelector('#eco-donut-canvas') as HTMLCanvasElement | null;
    if (!donutCanvas) return;
    donutCanvas.style.cursor = 'default';
    donutCanvas.addEventListener('mousemove', (e) => {
      const rect = donutCanvas.getBoundingClientRect();
      const mx = (e.clientX - rect.left) * (200 / rect.width);
      const my = (e.clientY - rect.top) * (200 / rect.height);
      const idx = donutHitTest(mx, my, currentDonutSegments);
      if (idx !== donutHoveredIndex) {
        donutHoveredIndex = idx;
        donutCanvas.style.cursor = idx >= 0 ? 'pointer' : 'default';
        drawDonutChart(donutCanvas, currentDonutSegments, formatCash(Math.round(p.netWorth)), donutHoveredIndex);
      }
    });
    donutCanvas.addEventListener('mouseleave', () => {
      donutHoveredIndex = -1;
      donutCanvas.style.cursor = 'default';
      drawDonutChart(donutCanvas, currentDonutSegments, formatCash(Math.round(p.netWorth)));
    });
  });

  if (!document.querySelector('#dash-link-style')) {
    const s = document.createElement('style');
    s.id = 'dash-link-style';
    s.textContent = `.dash-link{display:block;background:none;border:none;color:var(--cyan-dim);font-family:var(--sans);font-size:12px;cursor:pointer;text-align:left;padding:0;transition:color 0.12s}.dash-link:hover{color:var(--cyan)}`;
    document.head.appendChild(s);
  }

  // ── "Gebäude kaufen" opens purchase modal ──────────────────────────
  const buyBtn = wrapper.querySelector('#eco-btn-buy-building');
  if (buyBtn) {
    buyBtn.addEventListener('click', () => {
      openBuyBuildingModal(p.runners);
    });
  }

  root.appendChild(wrapper);

  // ── Draw charts + start animation (after DOM attachment) ───────────
  requestAnimationFrame(() => {
    const donutCanvas = wrapper.querySelector('#eco-donut-canvas') as HTMLCanvasElement | null;
    if (donutCanvas) {
      drawDonutChart(donutCanvas, assetSegments, formatCash(Math.round(p.netWorth)));
    }
    const sparkCanvas = wrapper.querySelector('#eco-nw-sparkline') as HTMLCanvasElement | null;
    if (sparkCanvas && p.netWorthHistory && p.netWorthHistory.length > 1) {
      drawSparkline(sparkCanvas, p.netWorthHistory, '#fca311');
    }
    const forecastSpark = wrapper.querySelector('#eco-forecast-sparkline') as HTMLCanvasElement | null;
    if (forecastSpark && p.netWorthHistory && p.netWorthHistory.length > 1) {
      drawSparkline(forecastSpark, p.netWorthHistory, '#00f5d4');
    }
    // Balance bar expand/collapse handlers
    wrapper.querySelectorAll('.eco-bal-expandable').forEach(row => {
      row.addEventListener('click', () => {
        const details = row.nextElementSibling as HTMLElement | null;
        if (!details || !details.classList.contains('eco-bal-details')) return;
        const chevron = row.querySelector('.eco-bal-chevron');
        const isOpen = details.style.display !== 'none';
        details.style.display = isOpen ? 'none' : '';
        chevron?.classList.toggle('open', !isOpen);
      });
    });
    // Subsystem-bucket collapse: clicking the bucket header toggles its body.
    wrapper.querySelectorAll('.eco-bal-bucket-head').forEach(head => {
      head.addEventListener('click', () => {
        head.parentElement?.classList.toggle('collapsed');
      });
    });
    // Animated Net Worth Ticker — ease-out count from 0 to current value
    const nwTicker = wrapper.querySelector('#eco-nw-ticker') as HTMLElement | null;
    if (nwTicker) {
      const targetNW = Math.round(p.netWorth);
      const tickerDuration = 1200; // ms
      const tickerStart = performance.now();
      function tickNW(now: number): void {
        const elapsed = now - tickerStart;
        const progress = Math.min(elapsed / tickerDuration, 1);
        // ease-out cubic: 1 - (1 - t)^3
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = Math.round(targetNW * eased);
        nwTicker!.textContent = formatCash(current);
        if (progress < 1) requestAnimationFrame(tickNW);
      }
      nwTicker.textContent = formatCash(0);
      requestAnimationFrame(tickNW);
    }

    // Start pipeline animation
    startAnimation();
    // Hydrate manager portraits in building cards
    hydratePortraits(wrapper);
  });

  // ── Phase 4: deep-link click-through on balance detail rows ─────
  //
  // Each detail row carries data-target="nodeId" or data-action="cmd"
  // attrs (DTO v2). Clicking jumps the player to the relevant screen
  // with the target pre-filtered. We attach a single delegated listener
  // on the overview wrapper to survive rerenders.
  wrapper.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement | null;
    if (!target) return;

    // v1.11.0: extorted-building card → map with building pre-selected
    const extortedCard = target.closest('.eco-extorted-card') as HTMLElement | null;
    if (extortedCard) {
      const nodeId = extortedCard.dataset.nodeId;
      if (nodeId) {
        const sid = state.sessionId;
        if (sid) location.hash = `#${sid}/map?b=${encodeURIComponent(nodeId)}`;
        else nav('map');
        ev.preventDefault();
        return;
      }
    }

    const row = target.closest('.eco-bal-detail-row') as HTMLElement | null;
    if (!row) return;
    const nodeId = row.dataset.target;
    const action = row.dataset.action;
    if (nodeId) {
      // Route to map with the building pre-selected via URL hash.
      const sid = state.sessionId;
      if (sid) location.hash = `#${sid}/map?b=${encodeURIComponent(nodeId)}`;
      else nav('map');
      ev.preventDefault();
    } else if (action) {
      // Route to map (planning overlay lives on the map screen) with
      // the action id hinted via URL hash.
      const sid = state.sessionId;
      if (sid) location.hash = `#${sid}/map?a=${encodeURIComponent(action)}`;
      else nav('map');
      ev.preventDefault();
    }
  });

  // ── Phase 4: Audit-Mitigation CTA ───────────────────────────────
  //
  // When audit risk is elevated, the audit-card gets a companion CTA
  // that deep-links to the Subs tab so the player can launder. Shown
  // only at WARN threshold or above to avoid notification fatigue.
  if (avgRisk > AUDIT_THRESH_WARN) {
    const auditCard = wrapper.querySelector('.eco-kpi-card[title]') as HTMLElement | null;
    if (auditCard) {
      const cta = document.createElement('button');
      cta.className = 'eco-audit-cta';
      cta.textContent = t('economy.audit_cta') ?? 'Dirty waschen';
      cta.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const tab = wrapper.querySelector('#etab-subs') as HTMLButtonElement | null;
        tab?.click();
      });
      auditCard.appendChild(cta);
    }
  }

  // ── Phase 3 deep-dive tabs: render their content once on mount ────
  // Clean Flow tab — legal income + expense cards.
  const cleanFlowGrid = wrapper.querySelector('#eco-clean-flow-grid') as HTMLElement | null;
  if (cleanFlowGrid) {
    const legalLines = [...incomeLines.filter(l => l.legal), ...expenseLines.filter(l => l.legal)];
    renderFlowCards(cleanFlowGrid, legalLines, 'var(--green)');
  }
  // Dirty Flow tab — dirty income + dirty expense cards.
  const dirtyFlowGrid = wrapper.querySelector('#eco-dirty-flow-grid') as HTMLElement | null;
  if (dirtyFlowGrid) {
    const dirtyLines = [...incomeLines.filter(l => !l.legal), ...expenseLines.filter(l => !l.legal)];
    renderFlowCards(dirtyFlowGrid, dirtyLines, 'var(--amber)');
  }

  // Launder-Queue widget on Dirty Flow tab. Shows current Dirty Cash,
  // Subs available to launder through (with capacity if configured),
  // and an estimate of cycles-to-clean at current throughput.
  const launderWidget = wrapper.querySelector('#eco-launder-widget') as HTMLElement | null;
  if (launderWidget) {
    if (subs.length === 0) {
      launderWidget.innerHTML = `<div style="padding:12px;color:var(--text-lo);font-style:italic">${t('economy.launder_no_subs') ?? 'Keine Tarnfirmen — Dirty Cash kann aktuell nicht gewaschen werden.'}</div>`;
    } else {
      const rows = subs.map(sub => `<div class="eco-launder-row">
        <span>${escHtml(sub.name)} <span style="color:var(--text-lo);font-size:10px">(${sub.tier})</span></span>
        <span style="color:var(--amber)">Risk ${Math.round(sub.risk)}%</span>
      </div>`).join('');
      launderWidget.innerHTML = `<div style="margin-bottom:8px;font-family:var(--mono);font-size:13px">
        <span style="color:var(--text-lo)">${t('economy.launder_pending') ?? 'Pending Dirty'}:</span>
        <span style="color:var(--amber);margin-left:6px">${formatCash(Math.round(p.dirtyCash))}</span>
      </div>
      ${rows}`;
    }
  }

  // Audit-Risk tab formula breakdown.
  const auditBreakdown = wrapper.querySelector('#eco-audit-breakdown') as HTMLElement | null;
  if (auditBreakdown) {
    const dirtyRatio = (p.cleanCash + p.dirtyCash) > 0 ? p.dirtyCash / (p.cleanCash + p.dirtyCash) : 0;
    const heatFactor = 1 + p.federalHeat / 100; // illustrative; server decides actual
    const synthRisk = avgRisk * heatFactor;
    auditBreakdown.innerHTML = `
      <div class="eco-audit-row"><span>${t('economy.audit_avg_risk') ?? 'Ø Subsidiary Risk'}</span><span style="color:${auditColor}">${auditPct}%</span></div>
      <div class="eco-audit-row"><span>${t('economy.audit_fed_heat') ?? 'Federal Heat'}</span><span>${Math.round(p.federalHeat)}/100</span></div>
      <div class="eco-audit-row"><span>${t('economy.audit_heat_factor') ?? 'Heat-Multiplikator'}</span><span>×${heatFactor.toFixed(2)}</span></div>
      <div class="eco-audit-row"><span>${t('economy.audit_dirty_ratio') ?? 'Dirty/Gesamt'}</span><span>${(dirtyRatio * 100).toFixed(1)}%</span></div>
      <div class="eco-audit-row"><span>${t('economy.audit_thresholds') ?? 'Schwellen (warn/crit)'}</span><span>${Math.round(AUDIT_THRESH_WARN * 100)}% / ${Math.round(AUDIT_THRESH_CRIT * 100)}%</span></div>
      <div class="eco-audit-row"><span>${t('economy.audit_current') ?? 'Aktuelles Risk'}</span><span style="color:${auditColor}">${(synthRisk * 100).toFixed(1)}%</span></div>
    `;
  }

  // Audit-Risk What-If slider: user drags Dirty-Cash down (simulating
  // launder), see how risk drops.
  const whatIf = wrapper.querySelector('#eco-audit-whatif') as HTMLElement | null;
  if (whatIf) {
    whatIf.innerHTML = `
      <label style="display:block;font-size:11px;color:var(--text-mid);margin-bottom:6px">
        ${t('economy.audit_whatif_label') ?? 'Dirty Cash washen'}:
        <span id="eco-whatif-amount" style="color:var(--amber);font-family:var(--mono)">$0</span>
      </label>
      <input type="range" min="0" max="${Math.max(1, Math.round(p.dirtyCash))}" value="0" class="eco-audit-whatif-slider" id="eco-whatif-slider" aria-label="${t('economy.audit_whatif_label') ?? 'What-if: launder amount'}">
      <div class="eco-audit-whatif-preview" id="eco-whatif-preview">${t('economy.audit_whatif_preview_idle') ?? 'Slider bewegen um Auswirkung zu sehen'}</div>
    `;
    const slider = whatIf.querySelector('#eco-whatif-slider') as HTMLInputElement;
    const amountLbl = whatIf.querySelector('#eco-whatif-amount') as HTMLElement;
    const preview = whatIf.querySelector('#eco-whatif-preview') as HTMLElement;
    slider?.addEventListener('input', () => {
      const washAmount = Number(slider.value);
      const newDirty = p.dirtyCash - washAmount;
      const newTotal = p.cleanCash + washAmount + newDirty;
      const newDirtyRatio = newTotal > 0 ? newDirty / newTotal : 0;
      amountLbl.textContent = formatCash(washAmount);
      preview.innerHTML = `Dirty: <span style="color:var(--amber)">${formatCash(newDirty)}</span> · Dirty-Ratio: <span style="color:${newDirtyRatio > AUDIT_THRESH_WARN ? 'var(--red)' : 'var(--green)'}">${(newDirtyRatio * 100).toFixed(1)}%</span>`;
    });
  }

  // ── Phase 4: Peer-Median toggle on the Übersicht header ────────
  //
  // Adds a "Vergleich Median" checkbox to the tab bar; when checked,
  // fetches /economy/peers and annotates the top-row KPI cards with
  // median reference chips ("med $X"). Off by default to avoid
  // clutter; choice persists in sessionStorage per-session.
  const tabBar = wrapper.querySelector('.eco-tabs') as HTMLElement | null;
  if (tabBar && state.sessionId) {
    const toggle = document.createElement('label');
    toggle.className = 'eco-peer-toggle';
    const saved = sessionStorage.getItem('eco_peer_toggle') === '1';
    toggle.innerHTML = `<input type="checkbox" ${saved ? 'checked' : ''}> ${t('economy.peer_toggle') ?? 'Peer-Vergleich'}`;
    tabBar.appendChild(toggle);
    const cb = toggle.querySelector('input') as HTMLInputElement;
    const renderPeerChips = (peers: EconomyPeersResponse | null): void => {
      wrapper.querySelectorAll('.eco-peer-chip').forEach(el => el.remove());
      if (!peers) return;
      const addChip = (cardSel: string, value: number): void => {
        const card = wrapper.querySelector(cardSel) as HTMLElement | null;
        const labelEl = card?.querySelector('.eco-kpi-label') as HTMLElement | null;
        if (!labelEl) return;
        const chip = document.createElement('span');
        chip.className = 'eco-peer-chip';
        chip.textContent = `${t('economy.peer_median_chip') ?? 'med'} ${formatCash(Math.round(value))}`;
        chip.title = t('economy.peer_chip_title') ?? 'Session-Median (anonymisiert)';
        labelEl.appendChild(chip);
      };
      const cards = wrapper.querySelectorAll('.eco-kpi-card');
      // KPI order: 0=NetWorth, 1=Income, 2=Expense, 3=Audit
      if (cards[1]) addChip('.eco-kpi-card:nth-child(2)', peers.median.legalIncome + peers.median.dirtyIncome);
      if (cards[2]) addChip('.eco-kpi-card:nth-child(3)', peers.median.fixedExpenses + peers.median.actionExpenses);
    };
    cb.addEventListener('change', () => {
      sessionStorage.setItem('eco_peer_toggle', cb.checked ? '1' : '0');
      if (!cb.checked) {
        renderPeerChips(null);
        return;
      }
      if (state.sessionId) {
        getEconomyPeers(state.sessionId).then(renderPeerChips).catch(() => { /* tolerate pre-1.10.2 server */ });
      }
    });
    if (saved && state.sessionId) {
      getEconomyPeers(state.sessionId).then(renderPeerChips).catch(() => { /* ignore */ });
    }
  }

  // ── Phase 4: Bottom-up forecast fetch + Runway recompute ────────
  //
  // Replaces the naive "cleanCash + net*7" forecast with the server's
  // ProjectForecast (server v1.10.3+). Assumptions surface as a
  // hover-tooltip on the info icon. Runway uses the projected per-day
  // net (assumptions[net_per_day]) for a smoother estimate.
  if (state.sessionId) {
    getEconomyForecast(state.sessionId, 7).then(fm => {
      const forecastText = wrapper.querySelector('#eco-forecast-text') as HTMLElement | null;
      const infoIcon    = wrapper.querySelector('#eco-forecast-assumptions') as HTMLElement | null;
      if (forecastText) {
        forecastText.innerHTML = `<span style="color:var(--cyan)">\u25B8</span> ${t('economy.forecast_text', { amount: formatCash(fm.projectedCleanEnd) })}`;
      }
      if (infoIcon) {
        infoIcon.style.display = '';
        infoIcon.title = fm.assumptions.map(a =>
          `${a.label}: ${a.value >= 0 ? '+' : ''}${formatCash(Math.round(a.value))}`
        ).join('\n');
      }
      // Recompute Runway from bottom-up net-per-day.
      const netAssumption = fm.assumptions.find(a => a.key === 'net_per_day');
      if (netAssumption && netAssumption.value < 0) {
        const newRunway = p.cleanCash / Math.abs(netAssumption.value);
        const runwayCard = wrapper.querySelector('.eco-runway .eco-cash-value') as HTMLElement | null;
        const runwayHint = wrapper.querySelector('.eco-runway .eco-cash-hint') as HTMLElement | null;
        if (runwayCard) {
          runwayCard.textContent = `${newRunway.toFixed(1)} ${t('economy.runway_days_unit') ?? 'Tage'}`;
          runwayCard.style.color = newRunway < 7 ? 'var(--red)' : newRunway < 14 ? 'var(--amber)' : 'var(--text-hi)';
        }
        if (runwayHint) {
          runwayHint.textContent = t('economy.runway_bottom_up_hint') ?? 'bottom-up · 5-Tage-Schnitt';
        }
      }
    }).catch(() => { /* tolerate pre-1.10.3 server */ });
  }

  // Economy Phase 3: fetch per-cycle history on mount + re-render the
  // vs-previous-cycle diff badges + historical charts when it resolves.
  if (state.sessionId) {
    refreshHistory(state.sessionId, (data) => {
      const prev = previousSnapshot(data);
      if (prev) {
        const incomeT  = p.dailyBalance?.income ?? [];
        const expenseT = p.dailyBalance?.expenses ?? [];
        const totalIncome  = incomeT.reduce((s, l) => s + l.amount, 0);
        const totalExpense = expenseT.reduce((s, l) => s + l.amount, 0);
        const updates: Record<string, string> = {
          income:  renderDeltaHTML(describeDelta(prev.legalIncome + prev.dirtyIncome, totalIncome), fmtCashDelta),
          expense: renderDeltaHTML(describeDelta(prev.fixedExpenses + prev.actionExpenses, totalExpense, true), fmtCashDelta),
          clean:   renderDeltaHTML(describeDelta(prev.cleanCash, p.cleanCash), fmtCashDelta),
          dirty:   renderDeltaHTML(describeDelta(prev.dirtyCash, p.dirtyCash, true), fmtCashDelta),
        };
        for (const [key, html] of Object.entries(updates)) {
          const el = root.querySelector(`[data-diff="${key}"]`);
          if (el) el.innerHTML = html;
        }
      }
      // Clean history chart: legal income vs fixed expenses.
      const cleanChart = wrapper.querySelector('#eco-clean-history') as HTMLCanvasElement | null;
      if (cleanChart) {
        drawHistoryChart(cleanChart, data, [
          { label: 'Legal Income', color: '#06d6a0', get: s => s.legalIncome },
          { label: 'Fixed Costs',  color: '#e63946', get: s => s.fixedExpenses },
        ]);
      }
      // Dirty history chart: dirty income vs action costs.
      const dirtyChart = wrapper.querySelector('#eco-dirty-history') as HTMLCanvasElement | null;
      if (dirtyChart) {
        drawHistoryChart(dirtyChart, data, [
          { label: 'Dirty Income', color: '#f4a261', get: s => s.dirtyIncome },
          { label: 'Action Costs', color: '#e63946', get: s => s.actionExpenses },
        ]);
      }
      // Phase 5: per-row mini-sparklines. Needs server v1.10.4+ byCategory
      // field. For each .eco-bal-spark placeholder built by renderRow,
      // extract that category's series from history snapshots and inject
      // the SVG. Missing byCategory data (older server) leaves the
      // placeholder empty — :empty CSS hides the gap.
      const sparks = wrapper.querySelectorAll<HTMLElement>('.eco-bal-spark[data-spark-key]');
      if (sparks.length > 0 && data.some(s => s.byCategory && Object.keys(s.byCategory).length > 0)) {
        sparks.forEach(sparkEl => {
          const key = sparkEl.dataset.sparkKey ?? '';
          if (!key) return;
          const series = categorySeries(data, key);
          const anyNonzero = series.some(v => v !== 0);
          if (!anyNonzero) return; // leave empty → CSS hides
          const color = sparkEl.style.color || 'currentColor';
          sparkEl.innerHTML = renderMiniSparkline(series, {
            color,
            title: `${key}: ${series.slice(-7).map(v => formatCash(v)).join(' → ')}`,
          });
        });
      }
    });
  }

  return root;
}

// ── Donut Chart Renderer ────────────────────────────────────────────────

interface DonutSegment {
  value: number;
  color: string;
  label: string;
}

function drawDonutChart(canvas: HTMLCanvasElement, segments: DonutSegment[], centerText: string, hoveredIndex = -1): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1;
  const size = 200;
  canvas.width = size * dpr;
  canvas.height = size * dpr;
  canvas.style.width = size + 'px';
  canvas.style.height = size + 'px';
  ctx.scale(dpr, dpr);

  const cx = size / 2;
  const cy = size / 2;
  const outerR = 85;
  const innerR = 55;
  const gap = 0.02; // small gap between segments (radians)
  const total = segments.reduce((s, seg) => s + seg.value, 0);

  if (total === 0) {
    // Draw empty ring
    ctx.beginPath();
    ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
    ctx.arc(cx, cy, innerR, Math.PI * 2, 0, true);
    ctx.closePath();
    ctx.fillStyle = 'rgba(30,42,58,0.5)';
    ctx.fill();
  } else {
    let angle = -Math.PI / 2;
    for (let si = 0; si < segments.length; si++) {
      const seg = segments[si];
      const sweep = (seg.value / total) * Math.PI * 2;
      const startA = angle + gap / 2;
      const endA = angle + sweep - gap / 2;
      if (endA > startA) {
        const isHovered = si === hoveredIndex;
        const r = isHovered ? outerR + 4 : outerR;
        if (isHovered) {
          ctx.shadowColor = seg.color;
          ctx.shadowBlur = 12;
        }
        ctx.beginPath();
        ctx.arc(cx, cy, r, startA, endA);
        ctx.arc(cx, cy, innerR, endA, startA, true);
        ctx.closePath();
        ctx.fillStyle = isHovered ? seg.color : seg.color + 'cc';
        ctx.fill();
        if (isHovered) {
          ctx.shadowBlur = 0;
        }
      }
      angle += sweep;
    }
  }

  // Center text — show hovered segment info or default
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  if (hoveredIndex >= 0 && hoveredIndex < segments.length) {
    const seg = segments[hoveredIndex];
    const pct = total > 0 ? ((seg.value / total) * 100).toFixed(1) : '0';
    ctx.fillStyle = seg.color;
    ctx.font = '600 13px "Share Tech Mono", monospace';
    ctx.fillText(formatCash(Math.round(seg.value)), cx, cy - 8);
    ctx.fillStyle = 'rgba(180,190,200,0.7)';
    ctx.font = '10px "Share Tech Mono", monospace';
    ctx.fillText(`${pct}%`, cx, cy + 8);
  } else {
    ctx.fillStyle = '#e0e8f0';
    ctx.font = '600 15px "Share Tech Mono", monospace';
    ctx.fillText(centerText, cx, cy);
  }
}

// ── Sparkline Renderer ──────────────────────────────────────────────────

export function drawSparkline(canvas: HTMLCanvasElement, data: number[], color: string): void {
  const ctx = canvas.getContext('2d');
  if (!ctx || data.length < 2) return;

  const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1;
  const w = canvas.clientWidth || 120;
  const h = canvas.clientHeight || 32;
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  canvas.style.width = w + 'px';
  canvas.style.height = h + 'px';
  ctx.scale(dpr, dpr);

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const padY = h * 0.1;

  const points: [number, number][] = data.map((v, i) => [
    (i / (data.length - 1)) * w,
    padY + (1 - (v - min) / range) * (h - 2 * padY),
  ]);

  // Gradient fill under the line
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, color + '33');
  grad.addColorStop(1, color + '00');
  ctx.beginPath();
  ctx.moveTo(points[0][0], points[0][1]);
  for (let i = 1; i < points.length; i++) ctx.lineTo(points[i][0], points[i][1]);
  ctx.lineTo(points[points.length - 1][0], h);
  ctx.lineTo(points[0][0], h);
  ctx.closePath();
  ctx.fillStyle = grad;
  ctx.fill();

  // Line
  ctx.beginPath();
  ctx.moveTo(points[0][0], points[0][1]);
  for (let i = 1; i < points.length; i++) ctx.lineTo(points[i][0], points[i][1]);
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Endpoint dot
  const last = points[points.length - 1];
  ctx.beginPath();
  ctx.arc(last[0], last[1], 2, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
}
